import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Plus,
  Trash2,
  Building2,
  Mic2,
  Users,
  LayoutGrid,
  Swords,
  Sparkles,
  Globe,
  FileUp,
  AlertCircle,
  ExternalLink,
  ShoppingBag,
  ImageIcon,
  Activity,
  Fingerprint,
  Library,
  Search,
  RefreshCw,
  X,
  History,
} from 'lucide-react';
import { NumberField } from '@/cowork/shared/NumberField';
import { cn } from '@/components/ui/utils';
import { toast } from 'sonner';
import { coworkApi, API_BASE_URL, type AutofillStage, type MarketBrief } from '../api/coworkApi';
import type { BrandGraph, Competitor, Promotable, LibraryFile, BrandPulse } from '../types/cowork';
import { AudiencesTab } from './brandos/AudiencesTab';
import { SiteLookCard } from './brandos/SiteLookCard';
import { StartOverDialog } from './brandos/StartOverDialog';
import { WikiRebuildDialog } from './WikiRebuildDialog';
import { rebuildCounts } from './wikiRebuildCounts';
import { isBuildRunning, siteChangeOf, type SiteChange } from './brandos/siteChange';
import { conflictOf, conflictText, saveBrandGraph, type BrandConflict } from './brandos/brandOsApi';
import { HistoryPanel } from './brandos/HistoryPanel';
import { useStaffState } from '@/cowork/useIsStaff';
import { CapMeter } from '@/cowork/launch/CapMeter';
import { toastStudioError, studioErrorText, capReachedOf, accessClosedOf } from '@/cowork/launch/studioErrors';

// ─── helpers ────────────────────────────────────
const toArray = (v: string | string[] | undefined): string[] =>
  Array.isArray(v) ? v : typeof v === 'string' ? v.split(',').map(s => s.trim()).filter(Boolean) : [];

const fromArray = (arr: string[]): string => arr.join(', ');

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

/**
 * Sections a client team can open (cowork/clientFeatures.ts `brand`): Competitors is staff only, so a client never
 * gets sent to a page that would turn them away.
 */
function clientCanOpen(tab: string): boolean {
  return tab !== 'competitors';
}

// ─── Tab definitions ─────────────────────────────
/**
 * Brand OS sections. Pulse = the summary, Brand Id = identity + voice + content style,
 * then what the brand sells, who buys it, who it competes with, and the documents behind it all.
 * Deep-linkable with ?section=<id>.
 */
const TABS = [
  { id: 'pulse', label: 'Pulse', Icon: Activity },
  { id: 'brand', label: 'Brand Id', Icon: Fingerprint },
  { id: 'promotables', label: 'Promotables', Icon: ShoppingBag },
  { id: 'audiences', label: 'Audiences', Icon: Users },
  { id: 'competitors', label: 'Competitors', Icon: Swords },
  { id: 'library', label: 'Library', Icon: Library },
] as const;
type TabId = typeof TABS[number]['id'];
const BRAND_SUBTABS = [
  { id: 'identity', label: 'Identity & art direction', Icon: Building2 },
  { id: 'voice', label: 'Voice & messaging', Icon: Mic2 },
  { id: 'content', label: 'Content style', Icon: LayoutGrid },
] as const;
type BrandSubtab = typeof BRAND_SUBTABS[number]['id'];
const readSection = (): TabId => { const q = new URLSearchParams(window.location.search).get('section'); return (TABS.some((t) => t.id === q) ? q : 'pulse') as TabId; };

// ─────────────────────────────────────────────────
//  Main Component
// ─────────────────────────────────────────────────
type CrawlStatus = 'idle' | 'crawling' | 'done' | 'error';

/** Visible steps of a website → Brand OS build, in the order the server runs them. */
const AUTOFILL_STEPS: Array<{ id: AutofillStage; label: string }> = [
  { id: 'crawling', label: 'Reading site' },
  { id: 'identity', label: 'Identity & voice' },
  { id: 'assets', label: 'Logos & colours' },
  { id: 'promotables', label: 'Promotables' },
  { id: 'audiences', label: 'Audiences' },
  { id: 'competitors', label: 'Competitors' },
  { id: 'profiles', label: 'Competitor profiles' },
];
type PdfStatus = 'idle' | 'uploading' | 'done' | 'error';

const BUILD_RUNNING_NOTICE = 'A build is already running: it will finish on its own.';
/** An informational line under the build box (not an error). */
function CrawlNotice({ text }: { text: string }) {
  return <p role="status" className="mt-2 text-[11px] text-[#667085]">{text}</p>;
}

export function BrandOSPage({ onBack, brandId, section, standalone }: { onBack: () => void; brandId?: string; section?: TabId; standalone?: boolean }) {
  const [activeTab, setActiveTabState] = useState<TabId>(section || readSection);
  useEffect(() => { if (section) setActiveTabState(section); }, [section]);
  const setActiveTab = (id: TabId) => {
    if (standalone) { // each section is its own page in the sidebar: navigate instead of switching tabs
      const seg = id === 'brand' ? 'brand-id' : id;
      window.history.pushState(null, '', `/agents/${seg}`); window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }
    setActiveTabState(id); const u = new URL(window.location.href); u.searchParams.set('section', id); window.history.replaceState(null, '', u.toString());
  };
  const [brandSubtab, setBrandSubtab] = useState<BrandSubtab>('identity');
  const [graph, setGraphState] = useState<BrandGraph | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  // A client (not Beatly staff, or not known yet): staff wording, staff tools and staff-only sections stay out.
  const { staff } = useStaffState();
  const client = staff !== true;
  // For the build poll (an effect that must not restart when /v1/me answers).
  const clientRef = useRef(client);
  useEffect(() => { clientRef.current = client; }, [client]);
  const [showHistory, setShowHistory] = useState(false);
  // A deep link to a section a client cannot open lands on Pulse instead of an empty page. Only once /v1/me says this
  // is a client: while it loads the section renders nothing, so a staff deep link (/agents/competitors) stays put.
  useEffect(() => { if (staff === false && !clientCanOpen(activeTab)) setActiveTabState('pulse'); }, [staff, activeTab]);
  // Someone else saved since this page loaded (the API answered 409): autosave stops until the person reloads.
  const [conflict, setConflict] = useState<BrandConflict | null>(null);
  // The graph as the person sees it, the server's lastUpdatedAt it was built on (sent as baseUpdatedAt), and
  // whether there are edits not saved yet. Refs, so a save that fires later sends the latest of each.
  const graphRef = useRef<BrandGraph | null>(null);
  const baseRef = useRef<string | null>(null);
  const dirtyRef = useRef(false);
  const conflictRef = useRef(false);
  const setGraph = useCallback((g: BrandGraph | null) => { graphRef.current = g; setGraphState(g); }, []);
  /** A graph from the server: shown, and the new base for the next save. Never over edits not saved yet. */
  const fromServer = useCallback((g: BrandGraph | null | undefined) => {
    if (!g || dirtyRef.current) return;
    baseRef.current = g.lastUpdatedAt || null;
    setGraph(g);
  }, [setGraph]);
  const [crawlStatus, setCrawlStatus] = useState<CrawlStatus>('idle');
  const [crawlError, setCrawlError] = useState<string | null>(null);
  // Calm, not an error: a build asked for while another is running (the API's 409 build_running).
  const [crawlNotice, setCrawlNotice] = useState<string | null>(null);
  const [autofillStage, setAutofillStage] = useState<AutofillStage | null>(null);
  // A build from another website waits for "Start over" in the dialog (the API answered 409 site_changed).
  const [siteChange, setSiteChange] = useState<SiteChange | null>(null);
  const [startingOver, setStartingOver] = useState(false);
  // After a start over the wiki still holds the old brand's Brand OS pages (the reset leaves the wiki alone). Once the
  // new build finishes we ask — never rebuild on our own: the wiki is shared work and replaced pages lose their text.
  const startedOver = useRef(false);
  const [wikiStale, setWikiStale] = useState<number | null>(null);
  const [showWikiRebuild, setShowWikiRebuild] = useState(false);
  // Bumped when a build is started so the status poll below (re)starts — it used to run only on mount,
  // which left the page on "crawling" forever when the job failed a second later.
  const [pollTick, setPollTick] = useState(0);
  const [pdfStatus, setPdfStatus] = useState<PdfStatus>('idle');
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const pdfPreviewRevoke = useRef<string | null>(null);
  const [discoveryStatus, setDiscoveryStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [discoveryLog, setDiscoveryLog] = useState<string[]>([]);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [jobNodesCount, setJobNodesCount] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ga4PollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // GA4 integration state
  const [ga4Status, setGa4Status] = useState<{ connected: boolean; propertyName?: string }>({ connected: false });
  const [ga4Properties, setGa4Properties] = useState<Array<{ propertyId: string; displayName: string; accountName: string }>>([]);
  const [showPropertySelector, setShowPropertySelector] = useState(false);

  // Load brand graph on mount + check for background discovery job
  useEffect(() => {
    coworkApi.brands.get(brandId).then(g => {
      if (!g) return setGraph(g);
      baseRef.current = g.lastUpdatedAt || null;
      // Stale data: the map has companies the competitor list lacks. Shown merged; saved only with the person's next
      // edit (never on load: a silent full save would overwrite whatever someone else saved meanwhile).
      const nodes = g.competitor?.graph?.nodes ?? [];
      const namedCompetitors = g.competitor?.competitors?.filter(c => c.name) ?? [];
      if (nodes.length > 0 && namedCompetitors.length < nodes.length) {
        const existingNames = new Set(namedCompetitors.map(c => c.name.toLowerCase()));
        const synced = nodes
          .filter(n => n.name && !existingNames.has(n.name.toLowerCase()))
          .map(n => ({
            id: n.id,
            name: n.name,
            website: n.website,
            hq: n.hq,
            size: n.size,
            tagline: n.tagline,
            positioning: n.positioning,
            strengths: n.strengths ?? [],
            weaknesses: n.weaknesses ?? [],
            threatLevel: n.threatLevel,
            socialAccounts: n.socialAccounts,
          }));
        const repaired = {
          ...g,
          competitor: { ...g.competitor, competitors: [...namedCompetitors, ...synced] },
        };
        setGraph(repaired);
      } else {
        setGraph(g);
      }
    }).catch(console.error);

    // Check if a discovery job is running in the background
    coworkApi.brands.getDiscoveryStatus(brandId).then(job => {
      if (job.status === 'running') {
        setJobStatus('running');
        // Poll every 8s until complete (skip ticks while the tab is hidden)
        pollRef.current = setInterval(async () => {
          if (document.hidden) return;
          const latest = await coworkApi.brands.getDiscoveryStatus(brandId).catch(() => ({ status: 'error' as const }));
          if (latest.status !== 'running') {
            if (pollRef.current) clearInterval(pollRef.current);
            setJobStatus(latest.status as 'done' | 'error' | 'idle');
            if (latest.status === 'done') {
              setJobNodesCount((latest as { nodesCount?: number }).nodesCount ?? 0);
              // Refresh brand graph to get the new competitors
              coworkApi.brands.get(brandId).then(fromServer).catch(console.error);
            }
          }
        }, 8000);
      } else if (job.status === 'done') {
        setJobStatus('done');
        setJobNodesCount(job.nodesCount ?? 0);
      }
    }).catch(console.error);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [brandId, setGraph, fromServer]);

  // Load GA4 status on mount
  useEffect(() => {
    if (brandId) {
      coworkApi.ga4.status(brandId).then(setGa4Status).catch(() => {});
    }
  }, [brandId]);

  // GA4 connect handler (opens popup)
  const handleGA4Connect = useCallback(() => {
    const url = coworkApi.ga4.connectUrl(brandId);
    const popup = window.open(url, 'ga4-connect', 'width=600,height=700');
    if (ga4PollRef.current) clearInterval(ga4PollRef.current);
    ga4PollRef.current = setInterval(() => {
      if (!popup) {
        if (ga4PollRef.current) clearInterval(ga4PollRef.current);
        ga4PollRef.current = null;
        return;
      }
      if (popup.closed) {
        if (ga4PollRef.current) clearInterval(ga4PollRef.current);
        ga4PollRef.current = null;
        coworkApi.ga4.status(brandId).then(status => {
          setGa4Status(status);
          if (!status.propertyName) {
            coworkApi.ga4.properties(brandId).then(data => {
              setGa4Properties(data.properties);
              setShowPropertySelector(true);
            }).catch(() => { /* not connected yet — nothing to pick */ });
          }
        });
      }
    }, 1000);
  }, [brandId]);

  // Clear the GA4 popup poller on unmount
  useEffect(() => {
    return () => { if (ga4PollRef.current) clearInterval(ga4PollRef.current); };
  }, []);

  // GA4 property selection handler
  const handlePropertySelect = useCallback(async (propertyId: string) => {
    const prop = ga4Properties.find(p => p.propertyId === propertyId);
    if (!prop) return;
    await coworkApi.ga4.selectProperty(brandId || 'default', propertyId, prop.displayName);
    setGa4Status({ connected: true, propertyName: prop.displayName });
    setShowPropertySelector(false);
  }, [brandId, ga4Properties]);

  // GA4 disconnect handler
  const handleGA4Disconnect = useCallback(() => {
    setGa4Status({ connected: false });
    setShowPropertySelector(false);
    setGa4Properties([]);
  }, []);

  // Debounced auto-save of the latest graph, on the server version it was built on (baseUpdatedAt).
  const scheduleSave = useCallback(() => {
    if (conflictRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    dirtyRef.current = true;
    setSaveStatus('saving');
    debounceRef.current = setTimeout(async () => {
      debounceRef.current = null;
      const sent = graphRef.current;
      if (!sent) return;
      try {
        const saved = await saveBrandGraph(sent, brandId, baseRef.current);
        baseRef.current = saved?.lastUpdatedAt || baseRef.current;
        // Typed on while it saved: keep those edits (they save next); otherwise show what the server kept.
        if (graphRef.current === sent) { dirtyRef.current = false; if (saved) setGraph(saved); }
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 2000);
      } catch (err) {
        const c = conflictOf(err);
        if (c) { conflictRef.current = true; setConflict(c); setSaveStatus('conflict'); return; }
        setSaveStatus('error');
        if (capReachedOf(err) || accessClosedOf(err)) toastStudioError(err, 'brand');
      }
    }, 1200);
  }, [brandId, setGraph]);

  /**
   * A change to the graph. `serverAt` = the patch comes from a server answer that already saved it (its graph's
   * lastUpdatedAt): it becomes the new base and needs no save of its own, unless edits are waiting anyway.
   */
  const updateGraph = useCallback((patch: Partial<BrandGraph>, serverAt?: string) => {
    const prev = graphRef.current;
    if (!prev) return;
    setGraph({ ...prev, ...patch });
    if (serverAt) { baseRef.current = serverAt; if (dirtyRef.current) scheduleSave(); return; }
    scheduleSave();
  }, [scheduleSave, setGraph]);

  /** After a conflict: drop the local edits and show the Brand OS as it is now. */
  const reloadAfterConflict = useCallback(async () => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    dirtyRef.current = false;
    try {
      const g = await coworkApi.brands.get(brandId);
      conflictRef.current = false;
      setConflict(null);
      setSaveStatus('idle');
      baseRef.current = g?.lastUpdatedAt || null;
      setGraph(g);
    } catch {
      toast.error('Could not reload. Try again in a moment.');
    }
  }, [brandId, setGraph]);

  /**
   * Build the whole Brand OS from the site.
   *
   * The crawl, asset fetch and competitor discovery all run server-side now, so
   * this only starts the job and follows it. Previously the merge happened here
   * and was then overwritten by the Brandfetch response — which is built from
   * the *stored* graph — so everything outside brand identity was discarded.
   */
  /**
   * Follow a running build — including one started before this page was opened,
   * which is the point of running it server-side. Re-reads the graph each poll
   * so tabs fill in as each stage lands rather than all at the end.
   */
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      const job = await coworkApi.brands.autofillStatus(brandId).catch(() => null);
      if (cancelled || !job) return;

      if (job.status === 'running') {
        setCrawlStatus('crawling');
        setAutofillStage((job as { stage: AutofillStage }).stage);
        coworkApi.brands.get(brandId).then((g) => { if (!cancelled) fromServer(g); }).catch(() => {});
        timer = setTimeout(poll, 4000);
        return;
      }

      if (job.status === 'error') {
        startedOver.current = false;
        setCrawlNotice(null);
        setCrawlError((job as { error?: string }).error || 'Brand OS build failed');
        setCrawlStatus('error');
        setAutofillStage(null);
        return;
      }

      if (job.status === 'done') {
        const g = await coworkApi.brands.get(brandId).catch(() => null);
        if (cancelled) return;
        fromServer(g);
        setAutofillStage(null);
        setCrawlNotice(null);
        setCrawlStatus('done');
        setTimeout(() => { if (!cancelled) setCrawlStatus('idle'); }, 3000);
        if (startedOver.current && brandId) {
          startedOver.current = false;
          // The wiki is Beatly's (curated by staff, refused to clients by the API): a client gets no rebuild prompt.
          if (clientRef.current) return;
          const wikiBrand = brandId;
          // A dry run: how many wiki pages still come from the previous Brand OS (staff only).
          Promise.resolve()
            .then(() => coworkApi.wiki.rebuildFromBrandOs({ brandId: wikiBrand, dryRun: true }))
            .then((r) => { const n = rebuildCounts(r).replaced; if (!cancelled && n > 0) setWikiStale(n); })
            .catch(() => {});
        }
      }
    };

    void poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [brandId, pollTick, fromServer]);

  /** Another build is still running: say so calmly and follow it (the poll shows its steps) rather than fail. */
  const followRunningBuild = useCallback(() => {
    setCrawlNotice(BUILD_RUNNING_NOTICE);
    setCrawlStatus('crawling');
    setPollTick((t) => t + 1);
  }, []);

  const handleCrawl = useCallback(async (url: string) => {
    if (!url) return;
    setCrawlStatus('crawling');
    setCrawlError(null);
    setCrawlNotice(null);
    try {
      const job = await coworkApi.brands.autofill(url, brandId);
      setAutofillStage(job.stage);
      setPollTick((t) => t + 1);
    } catch (err) {
      // Another website than the one the Brand OS was built from: nothing started — ask before replacing it.
      const change = siteChangeOf(err, url);
      if (change) { setSiteChange(change); setCrawlStatus('idle'); return; }
      if (isBuildRunning(err)) { followRunningBuild(); return; }
      setCrawlError(studioErrorText(err, 'brand', 'Could not start the build'));
      setCrawlStatus('error');
    }
  }, [brandId, followRunningBuild]);

  const handleStartOver = useCallback(async (change: SiteChange) => {
    // An autosave still waiting would write the old Brand OS back over the fresh one.
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; setSaveStatus('idle'); }
    dirtyRef.current = false;
    setStartingOver(true);
    setCrawlError(null);
    setCrawlNotice(null);
    try {
      const job = await coworkApi.brands.autofill(change.url, brandId, { startOver: true });
      startedOver.current = true;
      setWikiStale(null);
      setSiteChange(null);
      setCrawlStatus('crawling');
      setAutofillStage(job.stage);
      coworkApi.brands.get(brandId).then(fromServer).catch(() => {});
      setPollTick((t) => t + 1);
    } catch (err) {
      setSiteChange(null);
      if (isBuildRunning(err)) { followRunningBuild(); return; }
      setCrawlError(studioErrorText(err, 'brand', 'Could not start over'));
      setCrawlStatus('error');
    } finally {
      setStartingOver(false);
    }
  }, [brandId, followRunningBuild, fromServer]);

  // Revoke blob URL on unmount
  useEffect(() => {
    return () => { if (pdfPreviewRevoke.current) URL.revokeObjectURL(pdfPreviewRevoke.current); };
  }, []);

  const handlePdfUpload = useCallback(async (file: File) => {
    if (!file || !graph) return;
    // Create a local blob URL for preview
    if (pdfPreviewRevoke.current) URL.revokeObjectURL(pdfPreviewRevoke.current);
    const blobUrl = URL.createObjectURL(file);
    pdfPreviewRevoke.current = blobUrl;
    setPdfPreviewUrl(blobUrl);
    setPdfFileName(file.name);
    setPdfStatus('uploading');
    setPdfError(null);
    try {
      // The document stays in the Library (searchable, curated into the wiki) — the identity extraction below is the quick win.
      coworkApi.brands.library.upload([file], brandId).catch(() => {});
      const info = await coworkApi.brands.uploadPdf(file);
      const now = graphRef.current || graph;
      updateGraph({
        brand: {
          ...now.brand,
          name: info.name || now.brand.name,
          industry: info.industry || now.brand.industry,
          description: info.description || now.brand.description,
          usp: info.usp || now.brand.usp,
          voiceTone: info.voiceTone || now.brand.voiceTone,
          voiceAttributes: info.voiceAttributes.length ? info.voiceAttributes : now.brand.voiceAttributes,
          writingStyle: info.writingStyle || now.brand.writingStyle,
          thingsToAvoid: info.thingsToAvoid.length ? info.thingsToAvoid : now.brand.thingsToAvoid,
          keyMessages: info.keyMessages.length ? info.keyMessages : now.brand.keyMessages,
          targetStatement: info.targetStatement || now.brand.targetStatement,
          primaryColors: info.primaryColors.length ? info.primaryColors : now.brand.primaryColors,
          fonts: info.fonts.length ? info.fonts : now.brand.fonts,
        },
      });
      setPdfStatus('done');
      setTimeout(() => setPdfStatus('idle'), 4000);
    } catch (err) {
      setPdfError(studioErrorText(err, 'brand', 'PDF extraction failed'));
      setPdfStatus('error');
    }
  }, [graph, updateGraph, brandId]);

  const handleDiscoverCompetitors = useCallback(async () => {
    if (!graph) return;
    setDiscoveryStatus('running');
    setDiscoveryLog([]);
    setDiscoveryError(null);
    try {
      await coworkApi.brands.discoverCompetitors(event => {
        if (event.type === 'error') {
          setDiscoveryError(event.message ?? 'Discovery failed');
          setDiscoveryStatus('error');
        } else if (event.type === 'result' && event.data) {
          coworkApi.brands.get(brandId).then(fresh => {
            fromServer(fresh);
          }).catch(console.error);
          setDiscoveryStatus('done');
        } else if (event.message) {
          setDiscoveryLog(prev => [...prev, event.message!]);
        }
      }, brandId);
    } catch (err) {
      setDiscoveryError(studioErrorText(err, 'brand', 'Discovery failed'));
      setDiscoveryStatus('error');
    }
  }, [graph, brandId, fromServer]);

  if (!graph) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-[#be43ff] animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-[#fafafa]">
      {/* Header */}
      <div className="shrink-0 px-8 pt-7 pb-0">
        <div className="max-w-[860px] mx-auto">
          {!standalone && (
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-[12px] text-[#667085] hover:text-[#344054] transition-colors mb-5"
            style={{ fontWeight: 500 }}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </button>
          )}
          <div className="flex items-end justify-between mb-5">
            <div>
              <h1 className="text-[22px] text-[#101828]" style={{ fontWeight: 700 }}>
                {standalone ? (TABS.find((t) => t.id === activeTab)?.label || 'Brand OS') : 'Brand OS'}
              </h1>
              <p className="text-[13px] text-[#667085] mt-0.5">
                {graph?.brand?.name ? `${graph.brand.name}: ` : ''}everything the agents know about this brand. Editable, ignited from a URL or a document.
              </p>
            </div>
            <div className="min-h-7 flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5">
              {saveStatus === 'saving' && (
                <span className="flex items-center gap-1.5 text-[12px] text-[#98a2b3]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Saving…
                </span>
              )}
              {saveStatus === 'saved' && (
                <span className="flex items-center gap-1.5 text-[12px] text-[#10b981]">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Saved
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="text-[12px] text-red-400">Save failed</span>
              )}
              {/* This month's use for a client team ("1 of 3 Brand OS autofills this month"); nothing for staff. */}
              <CapMeter studio="brand" unit="autofills" />
              <CapMeter studio="brand" unit="actions" />
              <button type="button" onClick={() => setShowHistory(true)} className="flex items-center gap-1.5 rounded-lg border border-[#d0d5dd] bg-white px-2.5 py-1 text-[12px] text-[#344054] hover:bg-[#f9fafb]" style={{ fontWeight: 600 }}>
                <History className="w-3.5 h-3.5" /> History
              </button>
            </div>
          </div>

          {/* Tabs — hidden when the section is its own sidebar page */}
          <div className={cn('flex gap-0 border-b border-gray-200', standalone && 'hidden')}>
            {TABS.filter(({ id }) => !client || clientCanOpen(id)).map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={cn(
                  'flex items-center gap-1.5 px-4 py-2.5 text-[13px] border-b-2 transition-colors -mb-px',
                  activeTab === id
                    ? 'border-[#be43ff] text-[#be43ff]'
                    : 'border-transparent text-[#667085] hover:text-[#344054]'
                )}
                style={{ fontWeight: activeTab === id ? 600 : 500 }}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="max-w-[860px] mx-auto">
          {conflict && (
            <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-[#fedf89] bg-[#fffaeb] px-4 py-3 text-[13px] text-[#93370d]">
              <span className="flex-1 min-w-[220px]">{conflictText(conflict)}</span>
              <button type="button" onClick={() => void reloadAfterConflict()} className="rounded-lg bg-[#101828] px-3 py-1.5 text-[12.5px] text-white hover:bg-[#344054]" style={{ fontWeight: 600 }}>Reload</button>
            </div>
          )}
          {wikiStale !== null && !client && (
            <div role="status" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-[#fedf89] bg-[#fffaeb] px-4 py-3 text-[13px] text-[#93370d]">
              <span className="flex-1 min-w-[220px]">Your wiki still has {wikiStale === 1 ? '1 page' : `${wikiStale} pages`} from the previous brand: Rebuild from Brand OS?</span>
              <button type="button" onClick={() => setShowWikiRebuild(true)} className="rounded-lg bg-[#101828] px-3 py-1.5 text-[12.5px] text-white hover:bg-[#344054]" style={{ fontWeight: 600 }}>Rebuild from Brand OS</button>
              <button type="button" onClick={() => setWikiStale(null)} className="rounded-lg px-2 py-1.5 text-[12.5px] text-[#93370d] hover:bg-[#fef0c7]">Not now</button>
            </div>
          )}
          {activeTab === 'pulse' && (
            <PulseTab brandId={brandId} graph={graph} client={client} go={(t) => { if (!client || clientCanOpen(t)) setActiveTab(t); }} canOpen={(t) => !client || clientCanOpen(t)} onCrawl={handleCrawl} crawlStatus={crawlStatus} crawlError={crawlError} crawlNotice={crawlNotice} autofillStage={autofillStage} onFiles={async (files) => {
              // Clients add documents to the Library; reading a guidelines PDF into the Brand Id is a staff tool.
              const pdf = client ? undefined : files.find((f) => f.type === 'application/pdf');
              if (pdf) await handlePdfUpload(pdf);
              const rest = files.filter((f) => f !== pdf);
              if (rest.length) { try { await coworkApi.brands.library.upload(rest, brandId); } catch (e) { toastStudioError(e, 'brand'); } }
            }} />
          )}
          {activeTab === 'brand' && (
            <div className="mb-4 flex gap-1 rounded-xl bg-white p-1 ring-1 ring-[#eaecf0] w-fit">
              {BRAND_SUBTABS.map(({ id, label, Icon }) => (
                <button key={id} type="button" onClick={() => setBrandSubtab(id)} className={cn('flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] transition-colors', brandSubtab === id ? 'bg-[#101828] text-white' : 'text-[#475467] hover:bg-[#f2f4f7]')} style={{ fontWeight: 600 }}><Icon className="h-3.5 w-3.5" /> {label}</button>
              ))}
            </div>
          )}
          {activeTab === 'brand' && brandSubtab === 'identity' && (
            <>
              <BrandIdentityTab
                graph={graph}
                onChange={updateGraph}
                onCrawl={handleCrawl}
                crawlStatus={crawlStatus}
                crawlError={crawlError}
                crawlNotice={crawlNotice}
                autofillStage={autofillStage}
                onPdfUpload={handlePdfUpload}
                pdfStatus={pdfStatus}
                pdfError={pdfError}
                pdfFileName={pdfFileName}
                pdfPreviewUrl={pdfPreviewUrl}
                brandId={brandId}
                client={client}
              />

              {/* ── Connected Services: GA4 ── */}
              <div className="mt-8 border border-gray-200 rounded-xl bg-white p-6">
                <h3 className="text-[15px] font-semibold text-[#344054] mb-1 flex items-center gap-2">
                  <span>Connected Services</span>
                </h3>
                <div className="mt-4 border border-gray-100 rounded-lg p-4 bg-[#fafafa]">
                  <div className="flex items-start gap-3">
                    <span className="text-xl mt-0.5">📊</span>
                    <div className="flex-1">
                      <h4 className="text-[14px] font-semibold text-[#344054]">Google Analytics 4</h4>
                      <p className="text-[12px] text-[#667085] mt-0.5 mb-3">
                        Connect your GA4 property to give agents access to real traffic data.
                      </p>

                      {!ga4Status.connected && !showPropertySelector && (
                        <button
                          onClick={handleGA4Connect}
                          className="inline-flex items-center gap-2 px-4 py-2 text-[13px] font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-[#344054] transition-colors"
                        >
                          🔗 Connect Google Analytics
                        </button>
                      )}

                      {showPropertySelector && ga4Properties.length > 0 && (
                        <div className="space-y-2">
                          <label className="block text-[12px] font-medium text-[#344054]">
                            Select a property
                          </label>
                          <select
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] bg-white text-[#344054]"
                            defaultValue=""
                            onChange={e => handlePropertySelect(e.target.value)}
                          >
                            <option value="" disabled>Choose a GA4 property...</option>
                            {ga4Properties.map(p => (
                              <option key={p.propertyId} value={p.propertyId}>
                                {p.displayName} ({p.accountName}): {p.propertyId}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {ga4Status.connected && ga4Status.propertyName && (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-[13px] text-green-700">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="font-medium">Connected: &quot;{ga4Status.propertyName}&quot; (GA4)</span>
                          </div>
                          <button
                            onClick={handleGA4Disconnect}
                            className="text-[12px] text-red-500 hover:text-red-700 underline mt-1"
                          >
                            Disconnect
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {activeTab === 'brand' && brandSubtab === 'voice' && (
            <VoiceTab graph={graph} onChange={updateGraph} />
          )}
          {activeTab === 'brand' && brandSubtab === 'content' && (
            <ContentTab graph={graph} onChange={updateGraph} />
          )}
          {activeTab === 'competitors' && (!client || clientCanOpen('competitors')) && (
            <CompetitorsTab
              graph={graph}
              onChange={updateGraph}
              onDiscover={handleDiscoverCompetitors}
              discoveryStatus={discoveryStatus}
              discoveryLog={discoveryLog}
              discoveryError={discoveryError}
              jobStatus={jobStatus}
              jobNodesCount={jobNodesCount}
              brandId={brandId}
            />
          )}
          {activeTab === 'audiences' && (
            <AudiencesTab graph={graph} onChange={updateGraph} brandId={brandId} />
          )}
          {activeTab === 'promotables' && (
            <PromotablesTab graph={graph} onChange={updateGraph} brandId={brandId} />
          )}
          {activeTab === 'library' && (
            <LibraryTab brandId={brandId} client={client} />
          )}
        </div>
      </div>
      <HistoryPanel brandId={brandId} open={showHistory} onClose={() => setShowHistory(false)} />
      <StartOverDialog change={siteChange} busy={startingOver} onCancel={() => setSiteChange(null)} onConfirm={(c) => void handleStartOver(c)} />
      {brandId && !client && <WikiRebuildDialog
        open={showWikiRebuild}
        brandId={brandId}
        onClose={() => setShowWikiRebuild(false)}
        onDone={(r) => { setShowWikiRebuild(false); setWikiStale(null); const c = rebuildCounts(r); toast.success(`Wiki rebuilt from the Brand OS: ${c.replaced} replaced with ${c.fresh} fresh pages`); }}
      />}
    </div>
  );
}

// ─────────────────────────────────────────────────
//  Shared form primitives
// ─────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-5">
      <label className="block text-[12px] text-[#344054] mb-1.5" style={{ fontWeight: 600 }}>
        {label}
        {hint && <span className="text-[#98a2b3] ml-1.5" style={{ fontWeight: 400 }}>{hint}</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  'w-full text-[13px] text-[#101828] bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-[#be43ff] focus:ring-2 focus:ring-[#be43ff]/10 transition-all placeholder:text-[#c4c9d4]';
const textareaCls = inputCls + ' resize-none';

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6 mb-4">
      <h3 className="text-[13px] text-[#101828] mb-5 pb-3 border-b border-gray-100" style={{ fontWeight: 700 }}>
        {title}
      </h3>
      {subtitle && <p className="text-[12px] text-[#667085] -mt-3 mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

// ─── Color swatch with inline color picker ────────────────────────────────────
function ColorSwatch({
  color, onColorChange, onRemove,
}: { color: string; onColorChange: (c: string) => void; onRemove: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isHex = /^#[0-9a-f]{3,8}$/i.test(color.trim());
  const bg = isHex ? color.trim() : '#e5e7eb';
  return (
    <div className="flex flex-col items-center gap-1 group relative">
      <div
        className="w-12 h-12 rounded-2xl cursor-pointer shadow-sm ring-1 ring-black/5 relative"
        style={{ background: bg }}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="color"
          value={isHex ? color.trim() : '#be43ff'}
          onChange={e => onColorChange(e.target.value)}
          className="absolute inset-0 opacity-0 w-full h-full cursor-pointer rounded-2xl"
        />
        <button
          onClick={e => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white border border-gray-200 shadow-sm text-[10px] text-[#667085] hover:bg-red-50 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        >×</button>
      </div>
      <span className="text-[9px] text-[#98a2b3] font-mono" style={{ letterSpacing: '0.02em' }}>
        {color.trim().toUpperCase()}
      </span>
    </div>
  );
}

// ─── Logo upload slot ─────────────────────────────────────────────────────────

function LogoSlot({
  label, hint, bg, slot, value, onChange, brandId,
}: {
  label: string;
  hint: string;
  bg: string;
  slot: string;
  value?: string;
  onChange: (url: string) => void;
  brandId?: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const result = await coworkApi.brands.uploadAsset(file, slot, brandId);
      onChange(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Support both local paths (/assets/...) and full URLs (https://cdn.brandfetch.io/...)
  const imgSrc = value
    ? (value.startsWith('http') ? value : `${API_BASE_URL}${value}`)
    : null;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-[#344054]" style={{ fontWeight: 600 }}>{label}</span>
        <span className="text-[10px] text-[#98a2b3]">{hint}</span>
      </div>
      <div
        className={cn(
          'relative rounded-2xl overflow-hidden border flex items-center justify-center cursor-pointer group transition-all',
          error ? 'border-red-200' : 'border-gray-100 hover:border-[#be43ff]/30',
        )}
        style={{ background: bg, height: 100 }}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        />
        {uploading ? (
          <Loader2 className="w-5 h-5 animate-spin text-[#be43ff]" />
        ) : imgSrc ? (
          <>
            <img src={imgSrc} alt={label} className="max-h-[80px] max-w-[80%] object-contain" />
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-[11px]" style={{ fontWeight: 600 }}>Replace</span>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1.5 opacity-40 group-hover:opacity-70 transition-opacity">
            <FileUp className="w-5 h-5" style={{ color: bg === '#ffffff' || bg === '#fafafa' ? '#667085' : '#fff' }} />
            <span className="text-[11px]" style={{ fontWeight: 500, color: bg === '#ffffff' || bg === '#fafafa' ? '#98a2b3' : 'rgba(255,255,255,0.7)' }}>
              Upload
            </span>
          </div>
        )}
        {value && !uploading && (
          <button
            onClick={e => { e.stopPropagation(); onChange(''); }}
            className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white/90 shadow text-[10px] text-[#667085] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
          >×</button>
        )}
      </div>
      {error && <span className="text-[10px] text-red-400">{error}</span>}
    </div>
  );
}

// ─────────────────────────────────────────────────
//  Brand Identity Tab
// ─────────────────────────────────────────────────

function BrandIdentityTab({
  graph,
  onChange,
  onCrawl,
  crawlStatus,
  crawlError,
  crawlNotice,
  autofillStage,
  onPdfUpload,
  pdfStatus,
  pdfError,
  pdfFileName,
  pdfPreviewUrl,
  brandId,
  client,
}: {
  graph: BrandGraph;
  onChange: (patch: Partial<BrandGraph>, serverAt?: string) => void;
  onCrawl: (url: string) => void;
  crawlStatus: CrawlStatus;
  crawlError: string | null;
  crawlNotice: string | null;
  autofillStage: AutofillStage | null;
  onPdfUpload: (file: File) => void;
  pdfStatus: PdfStatus;
  pdfError: string | null;
  pdfFileName: string | null;
  pdfPreviewUrl: string | null;
  brandId?: string;
  client?: boolean;
}) {
  const rawBrand = graph.brand;
  const b = {
    ...rawBrand,
    primaryColors: Array.isArray(rawBrand.primaryColors) ? rawBrand.primaryColors : [],
    fonts: Array.isArray(rawBrand.fonts) ? rawBrand.fonts : [],
    voiceAttributes: Array.isArray(rawBrand.voiceAttributes) ? rawBrand.voiceAttributes : [],
    keyMessages: Array.isArray(rawBrand.keyMessages) ? rawBrand.keyMessages : [],
    thingsToAvoid: Array.isArray(rawBrand.thingsToAvoid) ? rawBrand.thingsToAvoid : [],
  };
  const set = (k: keyof BrandGraph['brand'], v: unknown) =>
    onChange({ brand: { ...rawBrand, [k]: v } });

  return (
    <>
      {/* Website + crawl — top of form, prominent */}
      <div className="bg-gradient-to-r from-[#f8f5ff] to-[#fdf4ff] border border-[#be43ff]/15 rounded-2xl p-5 mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="w-4 h-4 text-[#be43ff]" />
          <span className="text-[13px] text-[#101828]" style={{ fontWeight: 700 }}>
            Auto-fill from your website
          </span>
        </div>
        <p className="text-[12px] text-[#667085] mb-3">
          Enter your URL and your agents will build the whole Brand OS from it: identity, voice, audience,
          content pillars and competitors. It keeps running if you leave this page.
        </p>
        <div className="flex gap-2">
          <input
            className={inputCls + ' flex-1'}
            value={b.website || ''}
            onChange={e => set('website', e.target.value)}
            placeholder="https://omniad.io"
            type="url"
          />
          <button
            onClick={() => onCrawl(b.website || '')}
            disabled={crawlStatus === 'crawling' || !b.website}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] transition-all shrink-0"
            style={{
              background: crawlStatus === 'done'
                ? '#ecfdf5'
                : crawlStatus === 'crawling'
                ? '#f8f5ff'
                : 'linear-gradient(135deg, #be43ff, #9b35d4)',
              color: crawlStatus === 'done'
                ? '#059669'
                : crawlStatus === 'crawling'
                ? '#be43ff'
                : 'white',
              fontWeight: 600,
              opacity: !b.website ? 0.4 : 1,
            }}
          >
            {crawlStatus === 'crawling' ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Building…</>
            ) : crawlStatus === 'done' ? (
              <><CheckCircle2 className="w-3.5 h-3.5" /> Filled!</>
            ) : (
              <><Sparkles className="w-3.5 h-3.5" /> Auto-fill</>
            )}
          </button>
        </div>
        {crawlError && (
          <p className="text-[11px] text-red-400 mt-2">{crawlError}</p>
        )}
        {crawlNotice && <CrawlNotice text={crawlNotice} />}
        {crawlStatus === 'crawling' && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {AUTOFILL_STEPS.map((step) => {
              const done = autofillStage ? AUTOFILL_STEPS.findIndex(x => x.id === autofillStage) > AUTOFILL_STEPS.indexOf(step) : false;
              const active = autofillStage === step.id;
              return (
                <span
                  key={step.id}
                  className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px]"
                  style={{
                    background: done ? '#ecfdf5' : active ? '#f8f5ff' : '#f9fafb',
                    color: done ? '#059669' : active ? '#be43ff' : '#98a2b3',
                  }}
                >
                  {done ? <CheckCircle2 className="w-3 h-3" />
                    : active ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <span className="w-3 h-3 rounded-full border border-current opacity-40" />}
                  {step.label}
                </span>
              );
            })}
          </div>
        )}
        {crawlStatus === 'done' && (
          <p className="text-[11px] text-[#059669] mt-2">
            Brand OS built! Review the tabs above and edit anything that needs adjusting.
          </p>
        )}
      </div>

      {/* PDF upload panel: a staff tool (clients add documents in the Library) */}
      {!client && <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden mb-4">
        <div className="p-5">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <FileUp className="w-4 h-4 text-[#667085]" />
              <span className="text-[13px] text-[#101828]" style={{ fontWeight: 700 }}>
                Upload brand guidelines PDF
              </span>
            </div>
            {(pdfStatus === 'uploading' || pdfStatus === 'done') && pdfFileName && (
              <span className="text-[11px] text-[#98a2b3] truncate max-w-[200px]">{pdfFileName}</span>
            )}
          </div>
          <p className="text-[12px] text-[#667085] mb-3">
            Your agents will read your brand guidelines and extract colors, fonts, tone of voice, key messages, and more.
          </p>

          {pdfStatus === 'uploading' ? (
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[#f8f5ff] text-[13px] text-[#be43ff]">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              <div>
                <span style={{ fontWeight: 600 }}>Reading document…</span>
                <span className="text-[12px] text-[#9b35d4] ml-1">This may take 15-30 seconds.</span>
              </div>
            </div>
          ) : pdfStatus === 'done' ? (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#ecfdf5] text-[12px] text-[#059669]" style={{ fontWeight: 600 }}>
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              Brand data extracted: review the fields below.
            </div>
          ) : (
            <label className={cn(
              'flex items-center justify-center gap-3 w-full rounded-xl border-2 border-dashed py-5 cursor-pointer transition-colors',
              pdfStatus === 'error' ? 'border-red-200 bg-red-50' : 'border-gray-200 hover:border-[#be43ff]/40 hover:bg-[#f8f5ff]/50'
            )}>
              <input type="file" accept="application/pdf" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) onPdfUpload(f); e.target.value = ''; }}
              />
              {pdfStatus === 'error' ? (
                <>
                  <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                  <span className="text-[12px] text-red-400" style={{ fontWeight: 500 }}>
                    {pdfError || 'Upload failed'}, <span className="underline">try again</span>
                  </span>
                </>
              ) : (
                <>
                  <FileUp className="w-5 h-5 text-[#c4c9d4] shrink-0" />
                  <span className="text-[12px] text-[#667085]" style={{ fontWeight: 500 }}>
                    Click to upload PDF <span className="text-[#c4c9d4]">· max 25 MB</span>
                  </span>
                </>
              )}
            </label>
          )}
        </div>

        {/* Embedded PDF preview */}
        {pdfPreviewUrl && (pdfStatus === 'uploading' || pdfStatus === 'done') && (
          <div className="border-t border-gray-100 relative">
            {pdfStatus === 'uploading' && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                <Loader2 className="w-5 h-5 text-[#be43ff] animate-spin" />
              </div>
            )}
            <iframe
              src={pdfPreviewUrl}
              className="w-full"
              style={{ height: 420, border: 'none', display: 'block' }}
              title="Brand guidelines preview"
            />
          </div>
        )}
      </div>}

      {/* Logo assets */}
      <Section title="Logo Assets">
        <p className="text-[12px] text-[#667085] mb-4 -mt-2">
          Upload your logo variants: agents will use these to generate ads, social posts, and branded content.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <LogoSlot brandId={brandId}
            label="Logo: Dark" hint="full wordmark · for light backgrounds"
            bg="#ffffff" slot="logoDark"
            value={b.logos?.logoDark}
            onChange={url => set('logos', { ...b.logos, logoDark: url })}
          />
          <LogoSlot brandId={brandId}
            label="Logo: Light" hint="full wordmark · for dark backgrounds"
            bg="#101828" slot="logoLight"
            value={b.logos?.logoLight}
            onChange={url => set('logos', { ...b.logos, logoLight: url })}
          />
          <LogoSlot brandId={brandId}
            label="Icon: Dark" hint="symbol/mark · for light backgrounds"
            bg="#fafafa" slot="iconDark"
            value={b.logos?.iconDark}
            onChange={url => set('logos', { ...b.logos, iconDark: url })}
          />
          <LogoSlot brandId={brandId}
            label="Icon: Light" hint="symbol/mark · for dark backgrounds"
            bg="#1a1a2e" slot="iconLight"
            value={b.logos?.iconLight}
            onChange={url => set('logos', { ...b.logos, iconLight: url })}
          />
        </div>
      </Section>

      <Section title="Core Identity">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Brand name">
            <input
              className={inputCls}
              value={b.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Omni Ad"
            />
          </Field>
          <Field label="Industry">
            <input
              className={inputCls}
              value={b.industry}
              onChange={e => set('industry', e.target.value)}
              placeholder="e.g. Music Technology"
            />
          </Field>
        </div>
        <Field label="Brand description" hint="2-3 sentences">
          <textarea
            className={textareaCls}
            rows={3}
            value={b.description}
            onChange={e => set('description', e.target.value)}
            placeholder="What does your brand do and who does it serve?"
          />
        </Field>
        <Field label="Unique selling proposition (USP)">
          <textarea
            className={textareaCls}
            rows={2}
            value={b.usp}
            onChange={e => set('usp', e.target.value)}
            placeholder="The one thing that makes you different from everyone else."
          />
        </Field>
        <Field label="Target statement" hint="who you're for">
          <textarea
            className={textareaCls}
            rows={2}
            value={b.targetStatement}
            onChange={e => set('targetStatement', e.target.value)}
            placeholder="e.g. For independent music producers who want to grow their audience and sell beats online."
          />
        </Field>
      </Section>
      <Section title="Visual Identity">
        <SiteLookCard brand={rawBrand} brandId={brandId} onBrand={(nb, saved) => {
          if (!saved) { onChange({ brand: nb }); return; }
          // Reading the look saved the graph on the server: its lastUpdatedAt is the new base, or the next edit would
          // conflict with this person's own change. Use the answer's when it has one, else read the graph again.
          if (saved.at) { onChange({ brand: nb }, saved.at); return; }
          void coworkApi.brands.get(brandId)
            .then((g) => onChange({ brand: g?.brand || nb }, g?.lastUpdatedAt || undefined))
            .catch(() => onChange({ brand: nb }));
        }} />
        {/* Color swatches */}
        <div className="mb-5">
          <label className="block text-[12px] text-[#344054] mb-2" style={{ fontWeight: 600 }}>
            Primary colors
            <span className="text-[#98a2b3] ml-1.5" style={{ fontWeight: 400 }}>click swatch to edit · click × to remove</span>
          </label>
          <div className="flex flex-wrap gap-3 mb-3">
            {b.primaryColors.map((color, i) => (
              <ColorSwatch
                key={i}
                color={color}
                onColorChange={newColor => {
                  const updated = [...b.primaryColors];
                  updated[i] = newColor;
                  set('primaryColors', updated);
                }}
                onRemove={() => set('primaryColors', b.primaryColors.filter((_, idx) => idx !== i))}
              />
            ))}
            {/* Add color button */}
            <label className="relative w-14 h-14 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:border-[#be43ff]/40 hover:bg-[#f8f5ff]/50 transition-colors shrink-0">
              <Plus className="w-4 h-4 text-[#c4c9d4]" />
              <input type="color" className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                onChange={e => set('primaryColors', [...b.primaryColors, e.target.value])}
              />
            </label>
          </div>
          {/* Raw text input for manual editing */}
          <input
            className={inputCls}
            value={fromArray(b.primaryColors)}
            onChange={e => set('primaryColors', toArray(e.target.value))}
            placeholder="#be43ff, #a9f600, #101828"
          />
        </div>

        {/* Font chips */}
        <div className="mb-2">
          <label className="block text-[12px] text-[#344054] mb-2" style={{ fontWeight: 600 }}>
            Fonts
          </label>
          {b.fonts.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {b.fonts.map((font, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-[#f9fafb] border border-gray-100"
                >
                  <span className="text-[15px] text-[#101828]" style={{ fontFamily: `'${font}', system-ui, sans-serif`, fontWeight: 600 }}>
                    Aa
                  </span>
                  <span className="text-[12px] text-[#344054]" style={{ fontWeight: 600 }}>{font}</span>
                  <button
                    onClick={() => set('fonts', b.fonts.filter((_, idx) => idx !== i))}
                    className="w-4 h-4 flex items-center justify-center text-[#c4c9d4] hover:text-[#667085] transition-colors"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <input
            className={inputCls}
            value={fromArray(b.fonts)}
            onChange={e => set('fonts', toArray(e.target.value))}
            placeholder="Inter, Satoshi, Roc Grotesk"
          />
        </div>
      </Section>

      {/* Visual Style */}
      <Section title="Visual Style" subtitle="Controls how AI-generated images look for this brand: applied to all agents.">
        <div className="mb-4">
          <label className="block text-[12px] text-[#344054] mb-1.5" style={{ fontWeight: 600 }}>
            Aesthetic
          </label>
          <input
            className={inputCls}
            value={b.visualStyle?.aesthetic || ''}
            onChange={e => set('visualStyle', { ...b.visualStyle, aesthetic: e.target.value })}
            placeholder='e.g. "UGC-like, authentic, raw" or "corporate-minimal" or "bold-editorial"'
          />
        </div>
        <div className="mb-4">
          <label className="block text-[12px] text-[#344054] mb-1.5" style={{ fontWeight: 600 }}>
            Style Directives
          </label>
          <textarea
            className={inputCls + ' resize-none'}
            rows={3}
            value={b.visualStyle?.directives?.join('\n') || ''}
            onChange={e => set('visualStyle', { ...b.visualStyle, directives: e.target.value.split('\n').filter(Boolean) })}
            placeholder={'One directive per line, e.g.:\nAlways use warm, natural lighting\nPhone-captured look, not studio\nShow real people, not stock models'}
          />
        </div>
        <div className="mb-4">
          <label className="block text-[12px] text-[#344054] mb-1.5" style={{ fontWeight: 600 }}>
            Preferred Formats
          </label>
          <input
            className={inputCls}
            value={fromArray(b.visualStyle?.preferredFormats || [])}
            onChange={e => set('visualStyle', { ...b.visualStyle, preferredFormats: toArray(e.target.value) })}
            placeholder="lifestyle photography, flat illustrations, 3D renders"
          />
        </div>
        <div className="mb-2">
          <label className="block text-[12px] text-[#344054] mb-1.5" style={{ fontWeight: 600 }}>
            Never Use in Visuals
          </label>
          <input
            className={inputCls}
            value={fromArray(b.visualStyle?.visualAvoid || [])}
            onChange={e => set('visualStyle', { ...b.visualStyle, visualAvoid: toArray(e.target.value) })}
            placeholder="stock photos, clip art, neon colors, text overlays"
          />
        </div>
      </Section>
    </>
  );
}

// ─────────────────────────────────────────────────
//  Voice & Messaging Tab
// ─────────────────────────────────────────────────

function VoiceTab({
  graph,
  onChange,
}: {
  graph: BrandGraph;
  onChange: (patch: Partial<BrandGraph>, serverAt?: string) => void;
}) {
  const b = graph.brand;
  const set = (k: keyof BrandGraph['brand'], v: unknown) =>
    onChange({ brand: { ...b, [k]: v } });

  return (
    <>
      <Section title="Brand Voice">
        <Field label="Overall tone">
          <input
            className={inputCls}
            value={b.voiceTone}
            onChange={e => set('voiceTone', e.target.value)}
            placeholder="e.g. Professional yet approachable. Confident and direct."
          />
        </Field>
        <Field label="Voice attributes" hint="comma-separated adjectives">
          <input
            className={inputCls}
            value={fromArray(b.voiceAttributes)}
            onChange={e => set('voiceAttributes', toArray(e.target.value))}
            placeholder="Bold, Witty, Educational, Empowering"
          />
        </Field>
        <Field label="Writing style" hint="how your agents should write for your brand">
          <textarea
            className={textareaCls}
            rows={3}
            value={b.writingStyle}
            onChange={e => set('writingStyle', e.target.value)}
            placeholder="Short punchy sentences. Use active voice. Avoid corporate jargon. Speak to creators like a peer, not a vendor."
          />
        </Field>
      </Section>
      <Section title="Messaging">
        <Field label="Key messages" hint="comma-separated: what you always want to communicate">
          <textarea
            className={textareaCls}
            rows={3}
            value={fromArray(b.keyMessages)}
            onChange={e => set('keyMessages', toArray(e.target.value))}
            placeholder="Omni Ad empowers creators to earn from their music, Build your brand not just your beats, Data-driven growth for independent artists"
          />
        </Field>
        <Field label="Things to avoid" hint="topics, phrases, or tones that are off-brand">
          <textarea
            className={textareaCls}
            rows={3}
            value={fromArray(b.thingsToAvoid)}
            onChange={e => set('thingsToAvoid', toArray(e.target.value))}
            placeholder="Don't mention competitor names, avoid technical jargon, never use the word 'synergy'"
          />
        </Field>
      </Section>
    </>
  );
}

// ─────────────────────────────────────────────────
//  Competitors Tab
// ─────────────────────────────────────────────────

const THREAT_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  high:   { bg: '#fff1f2', text: '#e11d48', dot: '#f43f5e' },
  medium: { bg: '#fffbeb', text: '#d97706', dot: '#f59e0b' },
  low:    { bg: '#f0fdf4', text: '#16a34a', dot: '#22c55e' },
};

const EDGE_LABELS: Record<string, string> = {
  'direct-rival': 'Direct rival',
  adjacent: 'Adjacent',
  'market-leader': 'Market leader',
  'niche-player': 'Niche player',
  upstream: 'Upstream',
  downstream: 'Downstream',
};

// ─── Rich competitor card ─────────────────────────────────────────────────────
const THREAT_BADGE = THREAT_COLORS; // alias

function CompetitorCard({
  competitor: c,
  index,
  cluster,
  onUpdate,
  onRemove,
  onProfile,
}: {
  competitor: Competitor;
  index: number;
  cluster?: { name: string; threat: string } | undefined;
  onUpdate: (patch: Partial<Competitor>) => void;
  onRemove: () => void;
  onProfile?: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [profiling, setProfiling] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const domain = c.website ? c.website.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';
  const faviconUrl = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=32` : null;
  const threat = c.threatLevel ? THREAT_BADGE[c.threatLevel] : null;
  const clusterThreat = cluster ? THREAT_BADGE[cluster.threat] ?? THREAT_BADGE.medium : null;

  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden mb-3 hover:border-gray-200 transition-colors">
      {/* Card header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            {faviconUrl && (
              <img
                src={faviconUrl}
                alt=""
                className="w-8 h-8 rounded-lg border border-gray-100 shrink-0 bg-gray-50"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[14px] text-[#101828]" style={{ fontWeight: 700 }}>{c.name || `Competitor ${index + 1}`}</span>
                {c.website && (
                  <a
                    href={c.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[11px] text-[#98a2b3] hover:text-[#be43ff] transition-colors"
                    onClick={e => e.stopPropagation()}
                  >
                    {domain}
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </div>
              {c.tagline && (
                <p className="text-[12px] text-[#667085] mt-0.5" style={{ fontStyle: 'italic' }}>"{c.tagline}"</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {threat && (
              <span className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: threat.bg, color: threat.text, fontWeight: 600 }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: threat.dot }} />
                {c.threatLevel} threat
              </span>
            )}
            {cluster && clusterThreat && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f3f4f6] text-[#667085]" style={{ fontWeight: 500 }}>
                {cluster.name}
              </span>
            )}
            {onProfile && (
              <button
                onClick={async () => { setProfiling(true); try { await onProfile(); setProfileOpen(true); } finally { setProfiling(false); } }}
                disabled={profiling}
                title="Audiences, promotables, voice and channels for this competitor: the same attributes your brand has"
                className="flex items-center gap-1 text-[11px] text-[#7c3aed] hover:text-[#5b21b6] px-2.5 py-1 rounded-lg hover:bg-[#f8f5ff] transition-colors disabled:opacity-60"
                style={{ fontWeight: 600 }}
              >
                {profiling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {c.profiledAt ? 'Re-profile' : 'Profile'}
              </button>
            )}
            <button
              onClick={() => setEditing(v => !v)}
              className="text-[11px] text-[#667085] hover:text-[#be43ff] px-2.5 py-1 rounded-lg hover:bg-[#f8f5ff] transition-colors"
              style={{ fontWeight: 500 }}
            >
              {editing ? 'Done' : 'Edit'}
            </button>
            <button
              onClick={onRemove}
              className="w-6 h-6 flex items-center justify-center rounded-lg text-[#c4c9d4] hover:text-red-400 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Meta row */}
        {(c.hq || c.size) && (
          <div className="flex flex-wrap gap-2 mb-3">
            {c.hq && (
              <span className="flex items-center gap-1 text-[11px] text-[#667085] bg-[#f9fafb] border border-gray-100 px-2.5 py-1 rounded-lg">
                📍 {c.hq}
              </span>
            )}
            {c.size && (
              <span className="flex items-center gap-1 text-[11px] text-[#667085] bg-[#f9fafb] border border-gray-100 px-2.5 py-1 rounded-lg">
                👥 {c.size}
              </span>
            )}
          </div>
        )}

        {/* Social accounts */}
        {c.socialAccounts && Object.values(c.socialAccounts).some(Boolean) && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {[
              { key: 'twitter',   label: '𝕏',    bg: '#000000', color: '#ffffff', title: 'X / Twitter' },
              { key: 'linkedin',  label: 'in',   bg: '#0077b5', color: '#ffffff', title: 'LinkedIn' },
              { key: 'instagram', label: 'IG',   bg: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', color: '#ffffff', title: 'Instagram' },
              { key: 'tiktok',    label: 'TT',   bg: '#010101', color: '#ffffff', title: 'TikTok' },
              { key: 'youtube',   label: 'YT',   bg: '#ff0000', color: '#ffffff', title: 'YouTube' },
              { key: 'facebook',  label: 'f',    bg: '#1877f2', color: '#ffffff', title: 'Facebook' },
            ]
              .filter(s => c.socialAccounts?.[s.key as keyof typeof c.socialAccounts])
              .map(s => (
                <a
                  key={s.key}
                  href={c.socialAccounts![s.key as keyof typeof c.socialAccounts]}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.title}
                  className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] shrink-0 hover:opacity-80 transition-opacity"
                  style={{ background: s.bg, color: s.color, fontWeight: 700 }}
                  onClick={e => e.stopPropagation()}
                >
                  {s.label}
                </a>
              ))
            }
          </div>
        )}

        {/* Positioning */}
        {c.positioning && (
          <div className="mb-3">
            <span className="text-[10px] text-[#98a2b3] uppercase tracking-wide mb-1 block" style={{ fontWeight: 700 }}>Positioning</span>
            <p className="text-[12px] text-[#344054]" style={{ lineHeight: '1.6' }}>{c.positioning}</p>
          </div>
        )}

        {/* Strengths & Weaknesses */}
        {(c.strengths.length > 0 || c.weaknesses.length > 0) && (
          <div className="grid grid-cols-2 gap-3">
            {c.strengths.length > 0 && (
              <div>
                <span className="text-[10px] text-[#059669] uppercase tracking-wide mb-1.5 block" style={{ fontWeight: 700 }}>Strengths</span>
                <div className="flex flex-wrap gap-1">
                  {c.strengths.map((s, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-md" style={{ background: '#ecfdf5', color: '#059669', fontWeight: 500 }}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {c.weaknesses.length > 0 && (
              <div>
                <span className="text-[10px] text-[#e11d48] uppercase tracking-wide mb-1.5 block" style={{ fontWeight: 700 }}>Weaknesses</span>
                <div className="flex flex-wrap gap-1">
                  {c.weaknesses.map((w, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-md" style={{ background: '#fff1f2', color: '#e11d48', fontWeight: 500 }}>
                      {w}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Expandable edit form */}
      {(c.profiledAt || (c.audiences && c.audiences.length > 0) || (c.promotables && c.promotables.length > 0)) && (
        <div className="border-t border-gray-100 bg-[#fafafa] px-5 py-3">
          <button type="button" onClick={() => setProfileOpen((v) => !v)} className="flex w-full items-center gap-2 text-left text-[12px] text-[#344054]" style={{ fontWeight: 600 }}>
            <Sparkles className="h-3 w-3 text-[#7c3aed]" /> Profile: {c.audiences?.length || 0} audience{(c.audiences?.length || 0) === 1 ? '' : 's'} · {c.promotables?.length || 0} promotable{(c.promotables?.length || 0) === 1 ? '' : 's'}{c.priceTier ? ` · ${c.priceTier}` : ''}
            <span className="ml-auto text-[11px] text-[#98a2b3]">{profileOpen ? 'hide' : 'show'}</span>
          </button>
          {profileOpen && (
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <div>
                <div className="text-[10.5px] uppercase tracking-wide text-[#98a2b3] mb-1.5" style={{ fontWeight: 600 }}>Audiences</div>
                <div className="space-y-2">
                  {(c.audiences || []).map((a, i) => (
                    <div key={i} className="rounded-lg bg-white px-3 py-2 ring-1 ring-[#eaecf0]">
                      <input value={a.name} onChange={(e) => onUpdate({ audiences: (c.audiences || []).map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} className="w-full bg-transparent text-[12.5px] text-[#101828] outline-none" style={{ fontWeight: 600 }} />
                      <textarea value={a.description} onChange={(e) => onUpdate({ audiences: (c.audiences || []).map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })} rows={2} className="mt-0.5 w-full resize-none bg-transparent text-[11.5px] text-[#667085] outline-none" />
                      {(a.ageRange || a.platforms?.length) ? <div className="text-[11px] text-[#98a2b3]">{[a.ageRange, a.platforms?.join(', ')].filter(Boolean).join(' · ')}</div> : null}
                    </div>
                  ))}
                  {!c.audiences?.length && <div className="text-[11.5px] text-[#98a2b3]">Not profiled yet.</div>}
                </div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-wide text-[#98a2b3] mb-1.5" style={{ fontWeight: 600 }}>Promotables</div>
                <div className="space-y-1.5">
                  {(c.promotables || []).map((p, i) => (
                    <div key={i} className="flex items-baseline gap-2 rounded-lg bg-white px-3 py-1.5 ring-1 ring-[#eaecf0]">
                      <input value={p.name} onChange={(e) => onUpdate({ promotables: (c.promotables || []).map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} className="min-w-0 flex-1 bg-transparent text-[12.5px] text-[#101828] outline-none" style={{ fontWeight: 600 }} />
                      {p.price && <span className="text-[11px] text-emerald-600">{p.price}</span>}
                      {p.category && <span className="text-[11px] text-[#98a2b3]">{p.category}</span>}
                    </div>
                  ))}
                </div>
                {(c.voiceSummary || c.usp || c.channels?.length || c.creatorMarketing) && (
                  <div className="mt-3 space-y-1 text-[11.5px] text-[#475467]">
                    {c.usp && <div><b>USP:</b> {c.usp}</div>}
                    {c.voiceSummary && <div><b>Voice:</b> {c.voiceSummary}</div>}
                    {c.channels?.length ? <div><b>Channels:</b> {c.channels.join(', ')}</div> : null}
                    {c.creatorMarketing && <div><b>Creators:</b> {c.creatorMarketing}</div>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {editing && (
        <div className="border-t border-gray-100 p-5 bg-[#fafafa] space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name">
              <input className={inputCls} value={c.name} onChange={e => onUpdate({ name: e.target.value })} placeholder="Company name" />
            </Field>
            <Field label="Website">
              <input className={inputCls} value={c.website} onChange={e => onUpdate({ website: e.target.value })} placeholder="https://example.com" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="HQ">
              <input className={inputCls} value={c.hq ?? ''} onChange={e => onUpdate({ hq: e.target.value })} placeholder="San Francisco, CA" />
            </Field>
            <Field label="Size">
              <input className={inputCls} value={c.size ?? ''} onChange={e => onUpdate({ size: e.target.value })} placeholder="200-500 employees" />
            </Field>
          </div>
          <Field label="Tagline">
            <input className={inputCls} value={c.tagline ?? ''} onChange={e => onUpdate({ tagline: e.target.value })} placeholder="Their key slogan or marketing tagline" />
          </Field>
          <Field label="Positioning" hint="how they position themselves">
            <textarea className={textareaCls} rows={2} value={c.positioning} onChange={e => onUpdate({ positioning: e.target.value })} placeholder="How they position themselves in the market" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Strengths" hint="comma-separated">
              <textarea className={textareaCls} rows={2} value={fromArray(c.strengths)} onChange={e => onUpdate({ strengths: toArray(e.target.value) })} placeholder="Large user base, brand recognition" />
            </Field>
            <Field label="Weaknesses" hint="comma-separated">
              <textarea className={textareaCls} rows={2} value={fromArray(c.weaknesses)} onChange={e => onUpdate({ weaknesses: toArray(e.target.value) })} placeholder="Weak creator tools, no analytics" />
            </Field>
          </div>
          <Field label="Threat level">
            <select
              className={inputCls}
              value={c.threatLevel ?? ''}
              onChange={e => onUpdate({ threatLevel: e.target.value as 'high' | 'medium' | 'low' | undefined })}
            >
              <option value="">Not set</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </Field>
        </div>
      )}
    </div>
  );
}

// ─── Market brief — pre-rendered market analysis, reused by every proposal ────
const BRIEF_MARKETS = ['Sweden', 'Norway', 'Denmark', 'Finland', 'Germany', 'France', 'Netherlands', 'United Kingdom', 'Spain', 'Italy'];
function MarketBriefCard({ brandId }: { brandId?: string }) {
  const [market, setMarket] = useState('Sweden');
  const [brief, setBrief] = useState<MarketBrief | null>(null);
  const [fresh, setFresh] = useState(false);
  const [busy, setBusy] = useState<'load' | 'refresh' | null>('load');
  const [open, setOpen] = useState(false);
  const load = useCallback(async (m: string) => {
    setBusy('load');
    try { const r = await coworkApi.brands.marketBrief(brandId, m); setBrief(r.brief); setFresh(r.fresh); } catch { setBrief(null); } finally { setBusy(null); }
  }, [brandId]);
  useEffect(() => { void load(market); }, [market, load]);
  const refresh = async () => {
    setBusy('refresh');
    try { const r = await coworkApi.brands.refreshMarketBrief(brandId, market); setBrief(r.brief); setFresh(true); setOpen(true); toast.success(`Market brief for ${market} rewritten`); }
    catch (e) { toastStudioError(e, 'brand'); } finally { setBusy(null); }
  };
  const when = brief ? new Date(brief.generatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  return (
    <div className="mb-4 rounded-2xl border border-[#eaecf0] bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-[#101828]">Market brief</div>
          <div className="mt-0.5 text-[12px] text-[#667085]">Snapshot, SWOT, competitor angles and trends per market: written once from this Brand OS and reused by every proposal's "Where we are" step. Refresh when the picture changes.</div>
        </div>
        <select value={market} onChange={(e) => setMarket(e.target.value)} className="rounded-lg border border-[#d0d5dd] px-2.5 py-1.5 text-[12px] text-[#344054]">{BRIEF_MARKETS.map((m) => <option key={m}>{m}</option>)}</select>
        <button type="button" onClick={refresh} disabled={busy !== null} className="flex items-center gap-1.5 rounded-lg bg-[#be43ff] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#a835e0] disabled:opacity-60">{busy === 'refresh' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {brief ? 'Rewrite' : 'Write the brief'}</button>
      </div>
      {busy === 'load' && <div className="mt-3 text-[12px] text-[#98a2b3]">Loading…</div>}
      {busy !== 'load' && !brief && <div className="mt-3 text-[12px] text-[#98a2b3]">No brief for {market} yet: the first proposal in this market writes one (about 20 s), or write it now.</div>}
      {brief && (
        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className={`rounded-full px-2 py-0.5 font-medium ${fresh ? 'bg-[#ecfdf3] text-[#027a48]' : 'bg-[#fffaeb] text-[#b54708]'}`}>{fresh ? 'Fresh' : 'Older than 14 days: proposals will rewrite it'}</span>
            <span className="text-[#667085]">Written {when} from {brief.basis.competitors} competitors, {brief.basis.promotables} promotables, {brief.basis.wikiPages} brand knowledge pages</span>
            <button type="button" onClick={() => setOpen((o) => !o)} className="ml-auto text-[#6941c6] hover:underline">{open ? 'Hide' : 'Show'}</button>
          </div>
          <p className="mt-2 text-[13px] leading-relaxed text-[#344054]">{brief.snapshot}</p>
          {open && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {(['strengths', 'weaknesses', 'opportunities', 'threats'] as const).map((k) => (
                <div key={k} className="rounded-xl bg-[#f9fafb] p-3"><div className="text-[11px] font-semibold uppercase tracking-wide text-[#667085]">{k}</div><ul className="mt-1 space-y-1 text-[12.5px] text-[#344054]">{brief.swot[k].map((x, i) => <li key={i}>• {x}</li>)}</ul></div>
              ))}
              <div className="rounded-xl bg-[#f9fafb] p-3"><div className="text-[11px] font-semibold uppercase tracking-wide text-[#667085]">Competitor angles</div><ul className="mt-1 space-y-1 text-[12.5px] text-[#344054]">{brief.competitors.map((c, i) => <li key={i}><b>{c.name}</b>: {c.angle}</li>)}</ul></div>
              <div className="rounded-xl bg-[#f9fafb] p-3"><div className="text-[11px] font-semibold uppercase tracking-wide text-[#667085]">Trends to ride</div><ul className="mt-1 space-y-1 text-[12.5px] text-[#344054]">{brief.trends.map((t, i) => <li key={i}>• {t}</li>)}</ul></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CompetitorsTab({
  graph,
  onChange,
  onDiscover,
  discoveryStatus,
  discoveryLog,
  discoveryError,
  jobStatus,
  jobNodesCount,
  brandId,
}: {
  graph: BrandGraph;
  onChange: (patch: Partial<BrandGraph>, serverAt?: string) => void;
  onDiscover: () => void;
  brandId?: string;
  discoveryStatus: 'idle' | 'running' | 'done' | 'error';
  discoveryLog: string[];
  discoveryError: string | null;
  jobStatus: 'idle' | 'running' | 'done' | 'error';
  jobNodesCount: number;
}) {
  const competitors = graph.competitor.competitors;
  const discoveryGraph = graph.competitor.graph;
  const [showGraph, setShowGraph] = useState(!!discoveryGraph);

  const setCompetitors = (updated: Competitor[]) =>
    onChange({ competitor: { ...graph.competitor, competitors: updated } });

  const addCompetitor = () =>
    setCompetitors([
      ...competitors,
      { id: crypto.randomUUID(), name: '', website: '', positioning: '', strengths: [], weaknesses: [] },
    ]);

  const removeCompetitor = (id: string) =>
    setCompetitors(competitors.filter(c => c.id !== id));

  const updateCompetitor = (id: string, patch: Partial<Competitor>) =>
    setCompetitors(competitors.map(c => (c.id === id ? { ...c, ...patch } : c)));

  return (
    <div>
      <MarketBriefCard brandId={brandId} />
      {/* Background job banner — shown when user navigated away during discovery */}
      {jobStatus === 'running' && discoveryStatus === 'idle' && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-[#f8f5ff] border border-[#be43ff]/20 mb-4">
          <Loader2 className="w-4 h-4 text-[#be43ff] animate-spin shrink-0" />
          <div>
            <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>
              Competitor discovery is running in the background…
            </span>
            <span className="text-[12px] text-[#667085] ml-1.5">
              This page will update automatically when it finishes.
            </span>
          </div>
        </div>
      )}
      {jobStatus === 'done' && discoveryStatus === 'idle' && jobNodesCount > 0 && competitors.length > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#ecfdf5] border border-emerald-100 mb-4">
          <CheckCircle2 className="w-4 h-4 text-[#059669] shrink-0" />
          <span className="text-[12px] text-[#059669]" style={{ fontWeight: 600 }}>
            Discovery completed while you were away: {jobNodesCount} competitors loaded.
          </span>
        </div>
      )}

      {/* Auto-discover panel */}
      <div className="bg-gradient-to-r from-[#f8f5ff] to-[#fdf4ff] border border-[#be43ff]/15 rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#be43ff]" />
            <span className="text-[13px] text-[#101828]" style={{ fontWeight: 700 }}>
              Auto-discover competitors
            </span>
          </div>
          {discoveryGraph && (
            <button
              onClick={() => setShowGraph(v => !v)}
              className="text-[12px] text-[#be43ff] hover:underline"
              style={{ fontWeight: 500 }}
            >
              {showGraph ? 'Hide' : 'View'} competitive graph ({discoveryGraph.nodes.length} companies)
            </button>
          )}
        </div>
        <p className="text-[12px] text-[#667085] mb-3">
          Your agents will find ~20 competitors from your brand URL, crawl their sites, and map market clusters and relationships.
        </p>

        {discoveryStatus === 'running' ? (
          <div className="space-y-1.5">
            {discoveryLog.map((msg, i) => (
              <div key={i} className="flex items-center gap-2 text-[12px] text-[#667085]">
                {i === discoveryLog.length - 1
                  ? <Loader2 className="w-3 h-3 text-[#be43ff] animate-spin shrink-0" />
                  : <CheckCircle2 className="w-3 h-3 text-[#10b981] shrink-0" />
                }
                {msg}
              </div>
            ))}
          </div>
        ) : discoveryStatus === 'done' ? (
          <div className="flex items-center gap-2 text-[12px] text-[#059669]">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span style={{ fontWeight: 600 }}>
              {discoveryGraph?.nodes.length ?? 0} competitors discovered across {discoveryGraph?.clusters.length ?? 0} market clusters.
            </span>
          </div>
        ) : discoveryStatus === 'error' ? (
          <div className="flex items-center gap-2 text-[12px] text-red-400 mb-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            {discoveryError}
          </div>
        ) : null}

        {discoveryStatus !== 'running' && (
          <button
            onClick={onDiscover}
            disabled={!graph.brand.name}
            className="mt-3 flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] transition-all"
            style={{
              background: 'linear-gradient(135deg, #be43ff, #9b35d4)',
              color: 'white',
              fontWeight: 600,
              opacity: !graph.brand.name ? 0.4 : 1,
            }}
          >
            <Sparkles className="w-3.5 h-3.5" />
            {discoveryStatus === 'done' ? 'Re-discover' : 'Discover competitors'}
          </button>
        )}
      </div>

      {/* Competitive graph visualization */}
      {showGraph && discoveryGraph && (
        <div className="mb-4 space-y-3">
          {/* Market map narrative */}
          {discoveryGraph.marketMap && (
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <h4 className="text-[12px] text-[#101828] mb-2 uppercase tracking-wide" style={{ fontWeight: 700 }}>
                Market Map
              </h4>
              <p className="text-[12px] text-[#667085]" style={{ lineHeight: '1.7', whiteSpace: 'pre-line' }}>
                {discoveryGraph.marketMap}
              </p>
            </div>
          )}

          {/* Clusters — or fallback node list if clusters are empty */}
          {discoveryGraph.clusters.length > 0 ? (
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <h4 className="text-[12px] text-[#101828] mb-3 uppercase tracking-wide" style={{ fontWeight: 700 }}>
                Market Clusters
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {discoveryGraph.clusters.map((cluster, i) => {
                  const colors = THREAT_COLORS[cluster.threat] ?? THREAT_COLORS.medium;
                  return (
                    <div key={i} className="border border-gray-100 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[12px] text-[#101828]" style={{ fontWeight: 700 }}>{cluster.name}</span>
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1"
                          style={{ background: colors.bg, color: colors.text, fontWeight: 600 }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: colors.dot }} />
                          {cluster.threat} threat
                        </span>
                      </div>
                      <p className="text-[11px] text-[#667085] mb-2" style={{ lineHeight: '1.5' }}>{cluster.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {cluster.competitorNames.map(name => (
                          <span key={name} className="text-[10px] px-1.5 py-0.5 rounded-md bg-[#f9fafb] text-[#667085] border border-gray-100">
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : discoveryGraph.nodes.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <h4 className="text-[12px] text-[#101828] mb-3 uppercase tracking-wide" style={{ fontWeight: 700 }}>
                Discovered Companies ({discoveryGraph.nodes.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {discoveryGraph.nodes.map(node => (
                  <a
                    key={node.id}
                    href={node.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#f9fafb] border border-gray-100 hover:border-[#be43ff]/30 hover:bg-[#f8f5ff] transition-colors"
                  >
                    <span className="text-[12px] text-[#344054]" style={{ fontWeight: 600 }}>{node.name}</span>
                    <ExternalLink className="w-3 h-3 text-[#c4c9d4]" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Strategic insights */}
          {discoveryGraph.insights.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <h4 className="text-[12px] text-[#101828] mb-3 uppercase tracking-wide" style={{ fontWeight: 700 }}>
                Strategic Insights
              </h4>
              <ul className="space-y-2">
                {discoveryGraph.insights.map((insight, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[12px] text-[#344054]" style={{ lineHeight: '1.6' }}>
                    <span className="w-5 h-5 rounded-full bg-[#f3eeff] text-[#be43ff] text-[10px] flex items-center justify-center shrink-0 mt-0.5" style={{ fontWeight: 700 }}>
                      {i + 1}
                    </span>
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Relationship edges */}
          {discoveryGraph.edges.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <h4 className="text-[12px] text-[#101828] mb-3 uppercase tracking-wide" style={{ fontWeight: 700 }}>
                Key Relationships
              </h4>
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {discoveryGraph.edges.slice(0, 30).map((edge, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] text-[#667085] py-1 border-b border-gray-50 last:border-0">
                    <span className="text-[#344054]" style={{ fontWeight: 600 }}>{edge.from}</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#f3f4f6] text-[#667085]" style={{ fontWeight: 500 }}>
                      {EDGE_LABELS[edge.relationship] ?? edge.relationship}
                    </span>
                    <span className="text-[#344054]" style={{ fontWeight: 600 }}>{edge.to}</span>
                    {edge.notes && <span className="text-[#98a2b3] ml-auto truncate max-w-[160px]">{edge.notes}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manual competitors list */}
      {competitors.length === 0 && discoveryStatus === 'idle' && (
        <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center mb-4">
          <p className="text-[13px] text-[#98a2b3] mb-1">No competitors added yet.</p>
          <p className="text-[12px] text-[#c4c9d4]">
            Use Auto-discover above or add them manually below.
          </p>
        </div>
      )}

      {competitors.map((c, i) => (
        <CompetitorCard
          key={c.id}
          competitor={c}
          index={i}
          cluster={discoveryGraph?.clusters.find(cl => cl.competitorNames.includes(c.name))}
          onUpdate={patch => updateCompetitor(c.id, patch)}
          onRemove={() => removeCompetitor(c.id)}
          onProfile={async () => { try { const r = await coworkApi.brands.profileCompetitor(c.id, brandId); onChange({ competitor: r.graph.competitor }, r.graph.lastUpdatedAt); } catch (e) { toastStudioError(e, 'brand'); } }}
        />
      ))}

      <button
        onClick={addCompetitor}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-gray-200 text-[13px] text-[#667085] hover:border-[#be43ff]/40 hover:text-[#be43ff] hover:bg-[#f8f5ff] transition-all"
        style={{ fontWeight: 500 }}
      >
        <Plus className="w-4 h-4" />
        Add competitor manually
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────
//  Audience Tab
// ─────────────────────────────────────────────────

function ContentTab({
  graph,
  onChange,
}: {
  graph: BrandGraph;
  onChange: (patch: Partial<BrandGraph>, serverAt?: string) => void;
}) {
  const c = graph.content;
  const setContent = (patch: Partial<typeof c>) =>
    onChange({ content: { ...c, ...patch } });

  return (
    <>
      <Section title="Content Strategy">
        <Field label="Top performing formats" hint="comma-separated">
          <input
            className={inputCls}
            value={fromArray(c.topPerformingFormats)}
            onChange={e => setContent({ topPerformingFormats: toArray(e.target.value) })}
            placeholder="Short-form video, Tutorial, Behind the scenes, User-generated content"
          />
        </Field>
        <Field label="Top performing topics" hint="comma-separated">
          <input
            className={inputCls}
            value={fromArray(c.topPerformingTopics)}
            onChange={e => setContent({ topPerformingTopics: toArray(e.target.value) })}
            placeholder="Beat-making tips, Artist spotlight, Producer income, Studio setup"
          />
        </Field>
        <Field label="Publishing cadence" hint="optional">
          <input
            className={inputCls}
            value={c.publishingCadence || ''}
            onChange={e => setContent({ publishingCadence: e.target.value })}
            placeholder="3 posts/week on Instagram, 1 YouTube video/week"
          />
        </Field>
      </Section>

      <Section title="Content Pillars">
        {c.contentPillars.length === 0 && (
          <p className="text-[12px] text-[#98a2b3] mb-4">No pillars defined yet.</p>
        )}
        {c.contentPillars.map((pillar, i) => (
          <div key={pillar.id} className="flex items-start gap-3 mb-3 p-3 bg-[#f9fafb] rounded-xl">
            <div className="flex-1 grid grid-cols-3 gap-3">
              <input
                className={inputCls}
                value={pillar.name}
                onChange={e => {
                  const updated = [...c.contentPillars];
                  updated[i] = { ...updated[i], name: e.target.value };
                  setContent({ contentPillars: updated });
                }}
                placeholder="Pillar name"
              />
              <input
                className={inputCls}
                value={pillar.description}
                onChange={e => {
                  const updated = [...c.contentPillars];
                  updated[i] = { ...updated[i], description: e.target.value };
                  setContent({ contentPillars: updated });
                }}
                placeholder="What this covers"
              />
              <div className="flex items-center gap-2">
                <NumberField
                  className={inputCls}
                  min={0}
                  max={100}
                  allowEmpty
                  suffix="%"
                  value={pillar.percentage || null}
                  onChange={n => {
                    const updated = [...c.contentPillars];
                    updated[i] = { ...updated[i], percentage: n ?? 0 };
                    setContent({ contentPillars: updated });
                  }}
                  placeholder="%"
                />
                <button
                  onClick={() => {
                    const updated = c.contentPillars.filter((_, idx) => idx !== i);
                    setContent({ contentPillars: updated });
                  }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-[#c4c9d4] hover:text-red-400 hover:bg-red-50 transition-colors shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
        <button
          onClick={() =>
            setContent({
              contentPillars: [
                ...c.contentPillars,
                { id: crypto.randomUUID(), name: '', description: '', percentage: 0 },
              ],
            })
          }
          className="flex items-center gap-2 text-[12px] text-[#667085] hover:text-[#be43ff] transition-colors mt-1"
          style={{ fontWeight: 500 }}
        >
          <Plus className="w-3.5 h-3.5" />
          Add pillar
        </button>
      </Section>
    </>
  );
}

// ─── Promotables Tab ──────────────────────────────────────────────────────────

/** The audiences behind one promotable — primary first — and a way to assign it when none claims it yet. */
function WhoBuysThis({ graph, promotableId, brandId, onGraph }: { graph: BrandGraph; promotableId: string; brandId?: string; onGraph: (g: BrandGraph) => void }) {
  const [busy, setBusy] = useState(false); const [note, setNote] = useState<string | null>(null);
  const segs = graph.audience?.segments || [];
  const primary = segs.find((a) => (a.primaryPromotableIds || []).includes(promotableId));
  const also = segs.filter((a) => a.id !== primary?.id && (a.promotableIds || []).includes(promotableId));
  const size = (a: { metaEstimate?: { lower: number; upper: number } | null }) => a.metaEstimate ? ` · ${a.metaEstimate.upper >= 1e6 ? `${(a.metaEstimate.lower / 1e6).toFixed(1)}-${(a.metaEstimate.upper / 1e6).toFixed(1)}M` : `${Math.round(a.metaEstimate.lower / 1e3)}-${Math.round(a.metaEstimate.upper / 1e3)}k`}` : '';
  const assign = async () => { setBusy(true); setNote(null); try { const r = await coworkApi.brands.assignPromotable(promotableId, brandId); onGraph(r.graph); setNote(`${r.created ? 'New audience' : 'Joined'}: ${r.reason}`); } catch (e) { setNote(studioErrorText(e, 'brand')); } finally { setBusy(false); } };
  return (
    <div className="mb-2 flex flex-wrap items-center gap-1.5 text-[11.5px]">
      <span className="uppercase tracking-wide text-[#98a2b3]" style={{ fontWeight: 700 }}>Who buys this</span>
      {primary ? <span className="rounded-full bg-[#101828] px-2.5 py-0.5 text-white" style={{ fontWeight: 600 }} title="Primary buyer group">★ {primary.name}{size(primary)}</span> : <span className="text-[#b54708]">no primary audience yet</span>}
      {also.map((a) => <span key={a.id} className="rounded-full bg-[#f2f4f7] px-2.5 py-0.5 text-[#344054]" style={{ fontWeight: 600 }}>{a.name}{size(a)}</span>)}
      <button type="button" onClick={assign} disabled={busy || !segs.length} title={segs.length ? 'Let the planner place this promotable in its buyer group' : 'Generate audiences first'} className="rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-[#344054] hover:bg-gray-50 disabled:opacity-50" style={{ fontWeight: 600 }}>{busy ? 'Assigning…' : primary ? 'Re-assign' : 'Assign audience'}</button>
      {note && <span className="text-[#667085]">{note}</span>}
    </div>
  );
}

function PromotablesTab({
  graph,
  onChange,
  brandId,
}: {
  graph: BrandGraph;
  onChange: (patch: Partial<BrandGraph>, serverAt?: string) => void;
  brandId?: string;
}) {
  const [crawlStatus, setCrawlStatus] = useState<'idle' | 'crawling' | 'done' | 'error'>('idle');
  const [crawlError, setCrawlError] = useState<string | null>(null);

  const items = graph.promotables?.items || [];
  const website = graph.brand.website || '';

  const setItems = (updated: Promotable[]) =>
    onChange({ promotables: { ...graph.promotables, items: updated, sourceUrl: graph.promotables?.sourceUrl } });

  const updateItem = (id: string, patch: Partial<Promotable>) =>
    setItems(items.map(p => (p.id === id ? { ...p, ...patch } : p)));

  const removeItem = (id: string) =>
    setItems(items.filter(p => p.id !== id));

  const handleDiscover = async () => {
    if (!website) return;
    setCrawlStatus('crawling');
    setCrawlError(null);
    try {
      const result = await coworkApi.brands.discoverProducts(website, brandId, items.length);
      console.log('[promotables] Discover result:', result);

      if (result.products.length === 0) {
        setCrawlError('No products found on this website. Try a different brand or add products manually.');
        setCrawlStatus('error');
        return;
      }

      // Reload full graph to get merged results from backend
      const fresh = await coworkApi.brands.get(brandId);
      console.log('[promotables] Fresh graph promotables:', fresh?.promotables?.items?.length, 'items');
      if (fresh?.promotables) {
        onChange({ promotables: fresh.promotables }, fresh.lastUpdatedAt);
      }
      setCrawlStatus('done');
      setTimeout(() => setCrawlStatus('idle'), 3000);
    } catch (err) {
      setCrawlError(studioErrorText(err, 'brand', 'Discovery failed'));
      setCrawlStatus('error');
    }
  };


  return (
    <>
      {/* Discovery section */}
      <div className="bg-gradient-to-r from-[#f0fdf4] to-[#f0f9ff] border border-emerald-200/50 rounded-2xl p-5 mb-4">
        <div className="flex items-start gap-3">
          <ShoppingBag className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
          <div className="flex-1">
            <h4 className="text-[13px] text-[#101828] mb-1" style={{ fontWeight: 600 }}>
              Discover products & services
            </h4>
            <p className="text-[12px] text-[#667085] mb-3">
              Your agents will crawl {website || 'your brand website'} and extract products with names, descriptions, prices, and images.
            </p>
            <button
              onClick={handleDiscover}
              disabled={crawlStatus === 'crawling' || !website}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] text-white transition-all disabled:opacity-50"
              style={{
                fontWeight: 600,
                background: crawlStatus === 'done' ? '#10b981' : '#be43ff',
              }}
            >
              {crawlStatus === 'crawling' ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Discovering...</>
              ) : crawlStatus === 'done' ? (
                <><CheckCircle2 className="w-4 h-4" /> Done</>
              ) : (
                <><Sparkles className="w-4 h-4" /> {items.length > 0 ? 'Find 10 more' : 'Discover products'}</>
              )}
            </button>
            {!website && (
              <p className="text-[11px] text-amber-500 mt-2">Set a website URL in Brand Identity first.</p>
            )}
            {crawlError && (
              <p className="text-[11px] text-red-400 mt-2">{crawlError}</p>
            )}
          </div>
        </div>
      </div>

      {/* Product list */}
      {items.length === 0 ? (
        <div className="text-center py-16 text-[#98a2b3]">
          <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-[13px]" style={{ fontWeight: 500 }}>No products yet</p>
          <p className="text-[12px] mt-1">Click "Discover products" to auto-fill from your website</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[12px] text-[#667085] mb-2" style={{ fontWeight: 500 }}>
            {items.length} product{items.length !== 1 ? 's' : ''} discovered
            {graph.promotables?.sourceUrl ? ` from ${graph.promotables.sourceUrl}` : ''}
          </p>
          {items.map((product) => (
            <div key={product.id} className="bg-white border border-gray-100 rounded-2xl p-4 group relative">
              <button
                onClick={() => removeItem(product.id)}
                className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-red-50 text-[#98a2b3] hover:text-red-400"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <div className="flex gap-4">
                {/* Product image */}
                <div className="w-20 h-20 shrink-0 rounded-xl bg-[#f9fafb] border border-gray-100 flex items-center justify-center overflow-hidden">
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-[#c4c9d4]" />
                  )}
                </div>
                {/* Product details */}
                <div className="flex-1 min-w-0">
                  <input
                    className="text-[14px] text-[#101828] bg-transparent border-none outline-none w-full mb-1 placeholder:text-[#c4c9d4]"
                    style={{ fontWeight: 600 }}
                    value={product.name}
                    onChange={e => updateItem(product.id, { name: e.target.value })}
                    placeholder="Product name"
                  />
                  <WhoBuysThis graph={graph} promotableId={product.id} brandId={brandId} onGraph={(g) => onChange({ audience: g.audience }, g.lastUpdatedAt)} />
                  <div className="flex flex-wrap gap-2 mb-2">
                    <input value={product.price || ''} onChange={e => updateItem(product.id, { price: e.target.value })} placeholder="Price" className="w-24 text-[12px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full outline-none placeholder:text-emerald-300" style={{ fontWeight: 600 }} />
                    <input value={product.currency || ''} onChange={e => updateItem(product.id, { currency: e.target.value.toUpperCase().slice(0, 3) })} placeholder="SEK" className="w-14 text-[12px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full outline-none placeholder:text-emerald-300" style={{ fontWeight: 600 }} />
                    <input value={product.category || ''} onChange={e => updateItem(product.id, { category: e.target.value })} placeholder="Category" className="w-32 text-[12px] text-[#667085] bg-[#f2f4f7] px-2 py-0.5 rounded-full outline-none placeholder:text-[#c4c9d4]" style={{ fontWeight: 500 }} />
                  </div>
                  <textarea
                    className="text-[12px] text-[#667085] bg-transparent border-none outline-none w-full resize-none placeholder:text-[#c4c9d4]"
                    rows={2}
                    value={product.description}
                    onChange={e => updateItem(product.id, { description: e.target.value })}
                    placeholder="Short description"
                  />
                  <div className="mt-1 flex items-center gap-1.5">
                    <ExternalLink className="w-3 h-3 text-[#c4c9d4] shrink-0" />
                    <input value={product.url || ''} onChange={e => updateItem(product.id, { url: e.target.value })} placeholder="https://… product page" className="min-w-0 flex-1 text-[11px] text-[#be43ff] bg-transparent outline-none placeholder:text-[#c4c9d4]" />
                    {product.url && <a href={product.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#98a2b3] hover:text-[#be43ff]">open</a>}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Load more */}
          <button
            onClick={handleDiscover}
            disabled={crawlStatus === 'crawling'}
            className="w-full py-3 text-[13px] text-[#be43ff] hover:bg-[#f8f5ff] rounded-xl border border-dashed border-[#be43ff]/30 transition-colors"
            style={{ fontWeight: 600 }}
          >
            {crawlStatus === 'crawling' ? (
              <><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading more...</>
            ) : (
              <><Plus className="w-4 h-4 inline mr-1" /> Load 10 more products</>
            )}
          </button>
        </div>
      )}

      {/* Manual add */}
      <button
        onClick={() => setItems([...items, {
          id: crypto.randomUUID(),
          name: '',
          description: '',
        }])}
        className="mt-4 flex items-center gap-2 text-[12px] text-[#667085] hover:text-[#344054] transition-colors"
        style={{ fontWeight: 500 }}
      >
        <Plus className="w-3.5 h-3.5" />
        Add product manually
      </button>
    </>
  );
}

// ─────────────────────────────────────────────────
//  Pulse — how complete and how alive the Brand OS is
// ─────────────────────────────────────────────────
function PulseTab({ brandId, graph, client, go, canOpen, onCrawl, crawlStatus, crawlError, crawlNotice, autofillStage, onFiles }: {
  brandId?: string; graph: BrandGraph; client?: boolean; go: (t: TabId) => void; canOpen: (t: TabId) => boolean; onCrawl: (url: string) => void; crawlStatus: CrawlStatus; crawlError: string | null; crawlNotice: string | null; autofillStage: AutofillStage | null; onFiles: (files: File[]) => Promise<void>;
}) {
  const [pulse, setPulse] = useState<BrandPulse | null>(null);
  const [url, setUrl] = useState(graph.brand.website || '');
  const [dropBusy, setDropBusy] = useState(false);
  useEffect(() => { let on = true; coworkApi.brands.pulse(brandId).then((p) => { if (on) setPulse(p); }).catch(() => {}); return () => { on = false; }; }, [brandId, graph.lastUpdatedAt, crawlStatus]);
  useEffect(() => { if (!url && graph.brand.website) setUrl(graph.brand.website); }, [graph.brand.website, url]);
  const sectionOf: Record<string, TabId> = { brand: 'brand', promotables: 'promotables', audiences: 'audiences', competitors: 'competitors', library: 'library' };
  const ring = (pct: number, size = 88) => { const r = (size - 10) / 2, c = 2 * Math.PI * r; return (
    <svg width={size} height={size} className="shrink-0"><circle cx={size / 2} cy={size / 2} r={r} stroke="#eaecf0" strokeWidth="8" fill="none" /><circle cx={size / 2} cy={size / 2} r={r} stroke={pct >= 80 ? '#12b76a' : pct >= 40 ? '#be43ff' : '#f79009'} strokeWidth="8" fill="none" strokeLinecap="round" strokeDasharray={`${(pct / 100) * c} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} /><text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="fill-[#101828]" style={{ fontSize: size / 4.2, fontWeight: 700 }}>{pct}%</text></svg>
  ); };
  return (
    <div className="space-y-4">
      {/* Ignite */}
      <div className="rounded-2xl border border-[#be43ff]/15 bg-gradient-to-r from-[#f8f5ff] to-[#fdf4ff] p-5">
        <div className="mb-1 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#be43ff]" /><span className="text-[13px] text-[#101828]" style={{ fontWeight: 700 }}>Ignite the Brand OS</span></div>
        <p className="mb-3 text-[12px] text-[#667085]">A website or a document is enough: identity, voice, promotables, audiences, competitors and their profiles get built in the background. Everything stays editable.</p>
        <div className="flex gap-2">
          <input className={inputCls + ' flex-1'} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://brand.com" type="url" />
          <button onClick={() => onCrawl(url)} disabled={crawlStatus === 'crawling' || !url} className="flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] text-white transition-all disabled:opacity-40" style={{ background: 'linear-gradient(135deg, #be43ff, #9b35d4)', fontWeight: 600 }}>
            {crawlStatus === 'crawling' ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Building…</> : <><Globe className="h-3.5 w-3.5" /> Build from website</>}
          </button>
          <label className={cn('flex shrink-0 cursor-pointer items-center gap-2 rounded-xl border border-[#d0d5dd] bg-white px-4 py-2.5 text-[13px] text-[#344054] hover:bg-[#f9fafb]', dropBusy && 'opacity-60')} style={{ fontWeight: 600 }}>
            {dropBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />} Add documents
            <input type="file" multiple accept=".pdf,.txt,.md,.csv,.json,.html,image/*,application/pdf,text/*" className="hidden" onChange={async (e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; if (!fs.length) return; setDropBusy(true); try { await onFiles(fs); } finally { setDropBusy(false); } }} />
          </label>
        </div>
        {crawlError && <p className="mt-2 text-[11px] text-red-400">{crawlError}</p>}
        {crawlNotice && <CrawlNotice text={crawlNotice} />}
        {crawlStatus === 'crawling' && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {AUTOFILL_STEPS.map((step) => { const done = autofillStage ? AUTOFILL_STEPS.findIndex((x) => x.id === autofillStage) > AUTOFILL_STEPS.indexOf(step) : false; const active = autofillStage === step.id; return (
              <span key={step.id} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px]" style={{ background: done ? '#ecfdf5' : active ? '#f8f5ff' : '#f9fafb', color: done ? '#059669' : active ? '#be43ff' : '#98a2b3' }}>
                {done ? <CheckCircle2 className="h-3 w-3" /> : active ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="h-3 w-3 rounded-full border border-current opacity-40" />} {step.label}
              </span>
            ); })}
          </div>
        )}
      </div>

      {!pulse ? <div className="flex items-center gap-2 py-8 text-[13px] text-[#98a2b3]"><Loader2 className="h-4 w-4 animate-spin" /> Reading the Brand OS…</div> : (
        <>
          <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
            <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-5">
              {ring(pulse.overall, 96)}
              <div><div className="text-[15px] text-[#101828]" style={{ fontWeight: 700 }}>Brand OS health</div><div className="text-[12px] text-[#667085]">{pulse.counts.promotables} promotables · {pulse.counts.audiences} audiences · {pulse.counts.competitors} competitors ({pulse.counts.competitorsProfiled} profiled)<br />{client ? '' : `${pulse.counts.wikiPages} wiki pages · `}{pulse.counts.libraryReady}/{pulse.counts.libraryFiles} library files ready</div></div>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-5">
              <div className="mb-2 text-[11px] uppercase tracking-wide text-[#98a2b3]" style={{ fontWeight: 600 }}>By section</div>
              <div className="space-y-2">
                {pulse.sections.map((sec) => {
                  const to = sectionOf[sec.id] || 'brand';
                  const row = (<>
                    <span className="w-28 text-[12.5px] text-[#344054]" style={{ fontWeight: 600 }}>{sec.label}</span>
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-[#eaecf0]"><span className="block h-full rounded-full" style={{ width: `${sec.percent}%`, background: sec.percent === 100 ? '#12b76a' : '#be43ff' }} /></span>
                    <span className="w-10 text-right text-[12px] tabular-nums text-[#667085]">{sec.percent}%</span>
                  </>);
                  // A section this person cannot open is shown, not linked (a link would send them away).
                  return canOpen(to)
                    ? <button key={sec.id} type="button" onClick={() => go(to)} className="flex w-full items-center gap-3 text-left">{row}</button>
                    : <div key={sec.id} data-section={sec.id} className="flex w-full items-center gap-3">{row}</div>;
                })}
              </div>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-gray-100 bg-white p-5">
              <div className="mb-2 text-[11px] uppercase tracking-wide text-[#98a2b3]" style={{ fontWeight: 600 }}>What to do next</div>
              {pulse.next.length ? <ul className="space-y-1.5">{pulse.next.map((n, i) => { const to = sectionOf[n.section] || 'brand'; return <li key={i}>{canOpen(to) ? <button type="button" onClick={() => go(to)} className="text-left text-[13px] text-[#344054] hover:text-[#be43ff]">→ {n.text}</button> : <span className="text-[13px] text-[#344054]">{n.text}</span>}</li>; })}</ul> : <div className="text-[13px] text-[#067647]">Nothing missing: the agents have the full picture.</div>}
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-5">
              <div className="mb-2 text-[11px] uppercase tracking-wide text-[#98a2b3]" style={{ fontWeight: 600 }}>Signals from the platform</div>
              {pulse.signals ? (
                <div className="space-y-1 text-[13px] text-[#344054]">
                  <div><b>{pulse.signals.campaigns}</b> campaign{pulse.signals.campaigns === 1 ? '' : 's'} on Beatly{pulse.signals.live ? `, ${pulse.signals.live} live` : ''}</div>
                  {pulse.signals.lastCampaign && <div>Latest: <b>{pulse.signals.lastCampaign.name}</b> · {pulse.signals.lastCampaign.status?.toLowerCase()} · {pulse.signals.lastCampaign.start}{pulse.signals.lastCampaign.end ? ` → ${pulse.signals.lastCampaign.end}` : ''}{pulse.signals.lastCampaign.budget ? ` · ${Number(pulse.signals.lastCampaign.budget).toLocaleString('sv-SE')} ${pulse.signals.lastCampaign.currency || ''}` : ''}</div>}
                  {!client && pulse.signals.managers.length > 0 && <div>Campaign managers: {pulse.signals.managers.join(', ')}</div>}
                </div>
              ) : <div className="text-[13px] text-[#98a2b3]">No campaign data for this brand yet.</div>}
              <div className="mt-3 rounded-lg bg-[#f8f9fb] px-3 py-2 text-[11.5px] text-[#667085]">Brand tracker: {pulse.brandTracker.note}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────
//  Audiences — buyer personas, usually per promotable
// ─────────────────────────────────────────────────
// ─────────────────────────────────────────────────
//  Library — the client's documents, searchable by the agents
// ─────────────────────────────────────────────────
function LibraryTab({ brandId, client }: { brandId?: string; client?: boolean }) {
  const [files, setFiles] = useState<LibraryFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Array<{ file: string; idx: number; text: string }> | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(() => coworkApi.brands.library.list(brandId).then((r) => setFiles(r.files)).catch(() => {}), [brandId]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (!files.some((f) => f.status === 'processing')) return; const t = setInterval(() => { if (!document.hidden) load(); }, 10_000); return () => clearInterval(t); }, [files, load]);
  const upload = async (list: File[]) => { if (!list.length) return; setBusy(true); setErr(null); try { await coworkApi.brands.library.upload(list, brandId); await load(); } catch (e) { setErr(studioErrorText(e, 'brand')); } finally { setBusy(false); } };
  const search = async () => { if (!q.trim()) return setHits(null); const r = await coworkApi.brands.library.search(q, brandId); setHits(r.hits); };
  const fmtSize = (n: number) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.round(n / 1e3)} KB`);
  return (
    <>
      <label className={cn('mb-4 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-200 bg-white py-8 transition-colors hover:border-[#be43ff]/40 hover:bg-[#f8f5ff]/50', busy && 'opacity-60')}
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); void upload(Array.from(e.dataTransfer.files)); }}>
        <input type="file" multiple className="hidden" accept=".pdf,.txt,.md,.csv,.json,.html,image/*,application/pdf,text/*" onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; void upload(fs); }} />
        {busy ? <Loader2 className="h-6 w-6 animate-spin text-[#be43ff]" /> : <FileUp className="h-6 w-6 text-[#c4c9d4]" />}
        <div className="text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>Drop brand books, decks, briefs, price lists, notes or screenshots</div>
        <div className="text-[11.5px] text-[#98a2b3]">PDF, text, CSV, Markdown, HTML, images · up to 40 MB each · read and indexed for the agents{client ? '' : ' and curated into the wiki'}</div>
        {err && <div className="text-[11.5px] text-red-400">{err}</div>}
      </label>
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-3 py-2">
        <Search className="h-4 w-4 text-[#98a2b3]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void search(); }} placeholder="Ask the library: what the agents will find, e.g. 'return policy' or 'tone rules'" className="flex-1 bg-transparent text-[13px] outline-none" />
        {hits && <button type="button" onClick={() => { setHits(null); setQ(''); }} className="text-[#98a2b3] hover:text-[#344054]"><X className="h-4 w-4" /></button>}
      </div>
      {hits && (
        <div className="mb-4 space-y-2">
          {hits.length === 0 && <div className="text-[12.5px] text-[#98a2b3]">No passages match.</div>}
          {hits.map((h, i) => <div key={i} className="rounded-xl border border-[#eaecf0] bg-white p-3"><div className="mb-1 text-[11px] text-[#7c3aed]" style={{ fontWeight: 600 }}>{h.file} · part {h.idx + 1}</div><div className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-[#344054]">{h.text}</div></div>)}
        </div>
      )}
      {files.length === 0 && !busy && <p className="py-6 text-center text-[13px] text-[#98a2b3]">Nothing in the library yet.</p>}
      <div className="space-y-2">
        {files.map((f) => (
          <div key={f.id} className="rounded-2xl border border-gray-100 bg-white p-4">
            <div className="flex items-start gap-3">
              <div className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[10px] uppercase', f.status === 'ready' ? 'bg-[#ecfdf3] text-[#067647]' : f.status === 'error' ? 'bg-[#fef3f2] text-[#b42318]' : 'bg-[#f8f5ff] text-[#7c3aed]')} style={{ fontWeight: 700 }}>{f.status === 'processing' ? <Loader2 className="h-4 w-4 animate-spin" /> : (f.name.split('.').pop() || 'doc').slice(0, 4)}</div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <a href={`${API_BASE_URL}${f.url}`} target="_blank" rel="noreferrer" className="truncate text-[13.5px] text-[#101828] hover:text-[#be43ff]" style={{ fontWeight: 600 }}>{f.name}</a>
                  <span className="text-[11px] text-[#98a2b3]">{fmtSize(f.size)}{f.chunks ? ` · ${f.chunks} passages` : ''}{!client && f.wikiPages?.length ? ` · ${f.wikiPages.length} wiki page${f.wikiPages.length === 1 ? '' : 's'}` : ''}</span>
                  {f.status === 'processing' && <span className="text-[11px] text-[#7c3aed]">{client ? 'reading and indexing…' : 'reading, indexing, curating…'}</span>}
                  {f.status === 'error' && <span className="text-[11px] text-[#b42318]">{f.error}</span>}
                </div>
                {f.summary && <p className="mt-1 text-[12.5px] leading-relaxed text-[#475467]">{f.summary}</p>}
                {f.facts && f.facts.length > 0 && (
                  <div className="mt-2">
                    <button type="button" onClick={() => setOpen(open === f.id ? null : f.id)} className="text-[11.5px] text-[#7c3aed]" style={{ fontWeight: 600 }}>{open === f.id ? 'Hide' : 'Show'} {f.facts.length} facts the agents remember</button>
                    {open === f.id && <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-[12px] text-[#344054]">{f.facts.map((x, i) => <li key={i}>{x}</li>)}</ul>}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button type="button" title="Read again" onClick={() => coworkApi.brands.library.reprocess(f.id, brandId).then(load)} className="rounded-lg p-1.5 text-[#98a2b3] hover:bg-[#f2f4f7] hover:text-[#344054]"><RefreshCw className="h-3.5 w-3.5" /></button>
                <button type="button" title="Remove" onClick={() => { if (window.confirm(`Remove "${f.name}" from the library?`)) coworkApi.brands.library.remove(f.id).then(load); }} className="rounded-lg p-1.5 text-[#98a2b3] hover:bg-red-50 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
